/** @format */

import { StatCalculatorService, StatHoliday } from 'src/app/services';
import {
	CDOTemplate,
	PayPeriodWeekNumber,
	DailyTimeEntry,
	DailyTimeEntryDetails,
	DaysOfTheWeek,
	DaysWorkedCounter,
	EntryTypeTime,
	PayPeriodEntry,
	TimeEntry,
	TimeEntryDetails,
	TimesheetEntryType,
	WorkWeekPredictedPattern,
	ModifiedDurationDayTemplate,
	ExceptionType,
	TimeEntryStatus,
	TimesheetStatus,
	Timesheet,
	TimesheetEntry2,
	TimesheetMetadata,
	TimesheetEntryColors,
	CalendarMonthlyTimesheets,
	TimesheetExceptionType,
	TimeentryDB,
	PayPeriod,
	ScheduledActingAssignment,
	UserTimesheet,
	EmploymentStatus,
	BankType,
	TimeentryDBByPeriod,
	User,
	UnionCode,
	ReduceCdoScheduledOnStat,
} from 'src/app/store/application';
import { TimeBank, BankType as TimeBankBankType } from 'src/app/models/timeBank';
import { Configuration, Profile } from 'src/app/models/profile';
import { exceptionTypes } from '../mock-data/input-data/exceptionTypesData';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

var statCalculatorService = new StatCalculatorService();
var cdoNeedsRollOver = false;

const EPOCH_TIME_CONVERSION = 24 * 60 * 60 * 1000;
const NUMBER_OF_TIME_ENTRIES_PER_TIMESHEET = 14;
const PAY_PERIOD_HALF_LENGTH = 7;

export const getAllTimesheetYearsAndPaydates = (timesheet: Timesheet, yearsArr: number[], payDatesArr: Date[]) => {
	if (!yearsArr.includes(timesheet.startDate.getFullYear())) {
		yearsArr.push(timesheet.startDate.getFullYear());
	}

	if (!yearsArr.includes(timesheet.endDate.getFullYear())) {
		yearsArr.push(timesheet.endDate.getFullYear());
	}

	if (!payDatesArr.includes(timesheet.payDate)) {
		payDatesArr.push(timesheet.payDate);
	}
};

export const convertTimesheetToCalendarFriendlyObj = (
	timesheets: Timesheet[],
	yearsArr: number[],
	calendarResultsArr: CalendarMonthlyTimesheets[]
) => {
	yearsArr.forEach((year) => {
		for (let month = 0; month < 12; month++) {
			let monthlyTimesheets: Timesheet[] = [];
			let payDates: Date[] = [];

			timesheets.forEach((timesheet) => {
				if (getIsTimesheetRelated(timesheet.startDate, timesheet.endDate, month, year)) {
					monthlyTimesheets.push(timesheet);
				}

				if (new Date(timesheet.payDate).getMonth() === month && new Date(timesheet.payDate).getFullYear() === year) {
					if (!payDates.some((payDate) => payDate.toString() === timesheet.payDate.toString())) {
						payDates.push(timesheet.payDate);
					}
				}
			});

			calendarResultsArr.push({
				year,
				month,
				timesheets: monthlyTimesheets,
				payDates,
			});
		}
	});
};

export const parseLoadedTimesheet = (timesheet: Timesheet): Timesheet => {
	return Object.assign({}, timesheet, {
		id: timesheet.id,
		startDate: new Date(timesheet.startDate),
		endDate: new Date(timesheet.endDate),
		payPeriod: timesheet.payPeriod,
		payDate: new Date(timesheet.payDate),
		payPeriodStartDate: new Date(timesheet.payPeriodStartDate),
		payPeriodIndex: timesheet.payPeriodIndex,
		status: timesheet.status,
		metadata: JSON.parse(timesheet.metadata.toString()), // metadata is expected to be a json string
		entries: JSON.parse(timesheet.entries.toString()), // entries is expected to be a json string
		savedDateTime: !!timesheet.savedDateTime ? new Date(timesheet.savedDateTime) : null,
		submittedDateTime: !!timesheet.submittedDateTime ? new Date(timesheet.submittedDateTime) : null,
		approvedDateTime: !!timesheet.approvedDateTime ? new Date(timesheet.approvedDateTime) : null,
		sendToJDEDateTime: !!timesheet.sendToJDEDateTime ? new Date(timesheet.sendToJDEDateTime) : null,
		rolledBackDateTime: !!timesheet.rolledBackDateTime ? new Date(timesheet.rolledBackDateTime) : null,
		savedBy: timesheet.savedBy,
		submittedBy: timesheet.submittedBy,
		approvedBy: timesheet.approvedBy,
		sendToJDEBy: timesheet.sendToJDEBy,
		rolledBackBy: timesheet.rolledBackBy,
	});
};

export const getDateIsWithinBounds = (dateStart: Date, dateEnd: Date, dateCompare: Date): boolean => {
	let newDateStart = new Date(dateStart).setHours(0, 0, 0, 0);
	let newDateEnd = new Date(dateEnd).setHours(0, 0, 0, 0);
	let newDateCompare = new Date(dateCompare).setHours(0, 0, 0, 0);
	return newDateCompare.valueOf() >= newDateStart.valueOf() && newDateCompare.valueOf() <= newDateEnd.valueOf();
};

export const getIsTimesheetRelated = (dateStart: Date, dateEnd: Date, month: number, year: number) => {
	let startMonth = dateStart.getMonth();
	let startYear = dateStart.getFullYear();
	let endMonth = dateEnd.getMonth();
	let endYear = dateEnd.getFullYear();

	return (startMonth === month && startYear === year) || (endMonth === month && endYear === year);
};

export const extractDateDetails = (dateExtract: Date) => {
	let day = getWeekDayName(dateExtract);
	let month = getMonthName(dateExtract.getFullYear(), dateExtract.getMonth());
	let date = dateExtract.getDate();
	let year = dateExtract.getFullYear();

	return { day, month, date, year };
};

export const getMonthName = (year: number, month: number): string => {
	return new Date(year, month).toLocaleString('en-us', { month: 'long' });
};

export const getWeekDayName = (date: Date): string => {
	return date.toLocaleDateString('en-us', { weekday: 'long' });
};

export const getFullMonthAndDayFromDate = (date: Date): string => {
	return date.toLocaleString('en-us', { month: 'long' }) + ' ' + date.getDate();
};

export const get12HourTimeFromDate = (date: Date): string => {
	return date.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
};

export const getDateString = (date: Date) => {
	let weekDay = getWeekDayName(date);
	let month = getMonthName(date.getFullYear(), date.getMonth());
	let dateStr = `${weekDay} ${month} ${date.getDate()}, ${date.getFullYear()}`;

	return dateStr;
};

export const getCurrentDate = () => {
	return new Date(new Date().setHours(0, 0, 0));
};

export const getBeginningOfDayDate = (date: Date): Date => {
	return getFutureDate(date, 0);
};

export const getTimesheetDataExistsForDate = (year: number, month: number, timesheet: Timesheet[]): boolean => {
	return timesheet.some(
		(timesheet) =>
			(timesheet.startDate.getFullYear() === year || timesheet.endDate.getFullYear() === year) &&
			(timesheet.startDate.getMonth() === month || timesheet.endDate.getMonth() === month)
	);
};

export const compareEmail = (email1: string, email2: string) => {
	return email1.toLowerCase() === email2.toLowerCase();
};

export const getTimesheetExceptionTypes = (): TimesheetExceptionType[] => {
	return [
		{ code: TimesheetEntryType.ACTING, active: true, entryText: 'Acting', colour: TimesheetEntryColors.REGULAR_DAY },
		{ code: TimesheetEntryType.BANKED, active: true, entryText: 'Banked time', colour: TimesheetEntryColors.VACATION },
		{ code: TimesheetEntryType.CDO, active: true, entryText: 'Use Compressed Day Off (CDO)', colour: TimesheetEntryColors.CDO },
		{ code: TimesheetEntryType.COUNCIL, active: true, entryText: 'Council-related overtime', colour: TimesheetEntryColors.OVERTIME },
		{ code: TimesheetEntryType.OVERTIME, active: true, entryText: 'Overtime', colour: TimesheetEntryColors.OVERTIME },
		{ code: TimesheetEntryType.REGULAR_DAY, active: true, entryText: 'Regular', colour: TimesheetEntryColors.REGULAR_DAY },
		{ code: TimesheetEntryType.SICK, active: true, entryText: 'Sick', colour: TimesheetEntryColors.SICK },
		{ code: TimesheetEntryType.STANDBY, active: true, entryText: 'Standby', colour: TimesheetEntryColors.STANDBY },
		{ code: TimesheetEntryType.STAT, active: true, entryText: 'Statutory holiday', colour: TimesheetEntryColors.STAT },
		{ code: TimesheetEntryType.TRAINING, active: true, entryText: 'Training', colour: TimesheetEntryColors.REGULAR_DAY },
		{ code: TimesheetEntryType.UNION, active: true, entryText: 'Union business', colour: TimesheetEntryColors.REGULAR_DAY },
		{ code: TimesheetEntryType.VACATION, active: true, entryText: 'Vacation', colour: TimesheetEntryColors.VACATION },
		{ code: TimesheetEntryType.OTHER, active: true, entryText: 'Other leave', colour: TimesheetEntryColors.OTHER },
	];
};

export const getEntryTypeColor = (entry: TimesheetEntry2): string => {
	let entryType = getTimesheetExceptionTypes().find((entryType) => entryType.code === entry.name || entryType.entryText === entry.name);
	return entryType?.colour || TimesheetEntryColors.OTHER;
};

export const getTimesheetEntryColor = (type: string): TimesheetEntryColors => {
	let color = TimesheetEntryColors.OTHER;

	switch (type) {
		case TimesheetEntryType.REGULAR_DAY:
			color = TimesheetEntryColors.REGULAR_DAY;
			break;
		case TimesheetEntryType.ACTING:
			color = TimesheetEntryColors.REGULAR_DAY;
			break;
		case TimesheetEntryType.OVERTIME:
			color = TimesheetEntryColors.OVERTIME;
			break;
		case TimesheetEntryType.SICK:
			color = TimesheetEntryColors.SICK;
			break;
		case TimesheetEntryType.VACATION:
			color = TimesheetEntryColors.VACATION;
			break;
		case TimesheetEntryType.CDO:
			color = TimesheetEntryColors.CDO;
			break;
		case TimesheetEntryType.BANKED:
			color = TimesheetEntryColors.VACATION;
			break;
		case TimesheetEntryType.TRAINING:
			color = TimesheetEntryColors.REGULAR_DAY;
			break;
		case TimesheetEntryType.OTHER:
			color = TimesheetEntryColors.OTHER;
			break;
		case TimesheetEntryType.STAT:
			color = TimesheetEntryColors.STAT;
			break;
		case TimesheetEntryType.UNION:
			color = TimesheetEntryColors.REGULAR_DAY;
			break;
		case TimesheetEntryType.STANDBY:
			color = TimesheetEntryColors.STANDBY;
			break;
		default:
			break;
	}

	return color;
};

export const getDayOfWeek = (dayNumber: number): string => {
	let day = dayNumber <= 7 ? dayNumber : dayNumber - 7;
	let standardDayOfWeekIndex = getDayOfWeekFromNumber(day);
	let standardDayOfWeekDay = convertNumberToDayOfWeek(standardDayOfWeekIndex);
	return standardDayOfWeekDay;
};

export const convertNumberToDayOfWeek = (day: number): string => {
	// let day = getDayOfWeekFromNumber(number);
	let days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

	// if (day === -1) {
	//     return "Unknown_Day";
	// }

	return days[day];
};

export const getDayOfWeekFromNumber = (dayNumber: number): DaysOfTheWeek => {
	switch (dayNumber) {
		case 1:
			return DaysOfTheWeek.Saturday;
		case 2:
			return DaysOfTheWeek.Sunday;
		case 3:
			return DaysOfTheWeek.Monday;
		case 4:
			return DaysOfTheWeek.Tuesday;
		case 5:
			return DaysOfTheWeek.Wednesday;
		case 6:
			return DaysOfTheWeek.Thursday;
		case 7:
			return DaysOfTheWeek.Friday;
		default:
			return DaysOfTheWeek.Unassigned;
	}
};

export const getExceptionFromTypeId = (typeId: number) => {
	switch (typeId) {
		case 1:
		case 2:
		case 3:
		case 4:
			return TimesheetEntryType.REGULAR_DAY;

		case 19:
			return TimesheetEntryType.CDO;

		case 6:
		case 7:
		case 8:
		case 9:
		case 12:
		case 13:
		case 14:
			return TimesheetEntryType.OVERTIME;
		case 21:
		case 22:
			return TimesheetEntryType.VACATION;
		case 23:
		case 24:
		case 25:
		case 26:
			return TimesheetEntryType.SICK;
		case 10: // Bank Stat Hours @ 1.5 x number of hours worked; should this be here?
		case 11: // Bank Stat Hours @ 1.0 x number of hours worked; should this be here?
		case 28:
		case 29:
			return TimesheetEntryType.STAT;
		case 41:
			return TimesheetEntryType.CDO;

		default:
			return TimesheetEntryType.OTHER;
	}
};

export const countNumDistinctDaysPopulated = (dateString: (string | Date)[]): number => {
	const individualWorkedDays: DaysWorkedCounter[] = [];

	dateString.forEach((dateEntry) => {
		let date = getFutureDate(dateEntry, 0);
		let day = date.getDay();

		if (!individualWorkedDays.some((workDay) => workDay.day === day)) {
			individualWorkedDays.push({ day, numberTimesWorked: 1 });
		} else {
			let index = individualWorkedDays.findIndex((workDay) => workDay.day === day);
			individualWorkedDays[index].numberTimesWorked++;
		}
	});

	return individualWorkedDays.length;
};

export const userIsUnion = (configuration: Configuration) => configuration.unionCode === UnionCode.Union;
export const userIsExemptSupport = (configuration: Configuration) => configuration.unionCode === UnionCode.ExemptSupport;
export const userIsExemptManager = (configuration: Configuration) => configuration.unionCode === UnionCode.ExemptManagement;
export const isUserCDOEnrolled = (configuration: Configuration) => configuration.cdo || false;
export const isUserAuxiliary = (configuration: Configuration) => configuration.employmentStatus === EmploymentStatus.AUX;

export const filterTimeEntryTypes = (configuration: Configuration) => {
	return getTimesheetExceptionTypes()
		.filter((o) => o.active)
		.filter((o) => !(o.code === TimesheetEntryType.OVERTIME && !(userIsUnion(configuration) || userIsExemptSupport(configuration)))) // Only union and exempt support can book overtime
		.filter((o) => !(o.code === TimesheetEntryType.UNION && !userIsUnion(configuration))) // Only union can book union business
		.filter((o) => !(o.code === TimesheetEntryType.COUNCIL && !userIsExemptManager(configuration))) // Only exempt manager can book Council OT
		.filter((o) => !(o.code === TimesheetEntryType.CDO && !isUserCDOEnrolled(configuration))) // Only staff enroled in the CDO program can book CDO
		.filter((o) => !(o.code === TimesheetEntryType.STANDBY && !(userIsUnion(configuration) || isUserAuxiliary(configuration))))
		.filter(
			(o) =>
				!(
					(o.code === TimesheetEntryType.BANKED ||
						o.code === TimesheetEntryType.CDO ||
						o.code === TimesheetEntryType.COUNCIL ||
						o.code === TimesheetEntryType.STAT ||
						o.code === TimesheetEntryType.VACATION ||
						o.code === TimesheetEntryType.OTHER) &&
					isUserAuxiliary(configuration)
				)
		); // Auxiliary staff can't book any of these
};

export const dayMatchesPredictedWorkWeek = (
	predictedWorkWeekPattern: WorkWeekPredictedPattern[],
	payPeriodEntry: PayPeriodEntry,
	daysFromPayPeriodStart: number
): boolean => {
	let date = getFutureDate(payPeriodEntry.startDate, daysFromPayPeriodStart);
	let day = date.getDay();

	return predictedWorkWeekPattern[day].predictedWorkDay;
};

export const getStatHolidays = (year: number) => {
	if (!statCalculatorService) {
		statCalculatorService = new StatCalculatorService();
	}

	return statCalculatorService.getStatHolidays(year);
};

export const isStatHolidayDate = (payPeriodEntry: PayPeriodEntry, daysFromPayPeriodStart: number, statHolidays: StatHoliday[]): boolean => {
	let date = getFutureDate(payPeriodEntry.startDate, daysFromPayPeriodStart);
	let statHolidayPresent = statHolidays.some(
		(stat) =>
			stat.observedDate.getFullYear() === date.getFullYear() &&
			stat.observedDate.getMonth() === date.getMonth() &&
			stat.observedDate.getDate() === date.getDate()
	);

	return statHolidayPresent;
};

export const getStatHolidayName = (payPeriodEntry: PayPeriodEntry, daysFromPayPeriodStart: number, statHolidays: StatHoliday[]) => {
	let date = getFutureDate(payPeriodEntry.startDate, daysFromPayPeriodStart);
	let statHoliday = statHolidays.filter(
		(stat) =>
			stat.observedDate.getFullYear() === date.getFullYear() &&
			stat.observedDate.getMonth() === date.getMonth() &&
			stat.observedDate.getDate() === date.getDate()
	);

	return statHoliday[0].name;
};

export const isCDODate = (cdoTemplateInfo: CDOTemplate, payPeriodEntry: PayPeriodEntry, daysFromPayPeriodStart: number): boolean => {
	if (cdoTemplateInfo!.cdoWeek === PayPeriodWeekNumber.weekOne && daysFromPayPeriodStart >= PAY_PERIOD_HALF_LENGTH) {
		return false;
	}

	if (cdoTemplateInfo!.cdoWeek === PayPeriodWeekNumber.weekTwo && daysFromPayPeriodStart < PAY_PERIOD_HALF_LENGTH) {
		return false;
	}

	let date = getFutureDate(payPeriodEntry.startDate, daysFromPayPeriodStart);
	let day = date.getDay();
	return day === cdoTemplateInfo!.day;
};

export const isModifiedDurationDay = (
	payPeriodEntry: PayPeriodEntry,
	daysFromPayPeriodStart: number,
	modifiedDayDurationTemplate: ModifiedDurationDayTemplate
): boolean => {
	if (modifiedDayDurationTemplate.modifiedDayWeek === PayPeriodWeekNumber.weekOne && daysFromPayPeriodStart >= PAY_PERIOD_HALF_LENGTH) {
		return false;
	}

	if (modifiedDayDurationTemplate.modifiedDayWeek === PayPeriodWeekNumber.weekTwo && daysFromPayPeriodStart < PAY_PERIOD_HALF_LENGTH) {
		return false;
	}

	let date = getFutureDate(payPeriodEntry.startDate, daysFromPayPeriodStart);
	let day = date.getDay();
	return day === modifiedDayDurationTemplate.day;
};

export const generateHoursEntryObject = (exceptionType: ExceptionType): TimeEntry => {
	return {
		exceptionType,
		projectId: 0,
		entries: [],
	};
};

export const generatePredictedTimesheetData = (
	predictedWorkWeekPattern: WorkWeekPredictedPattern[],
	payPeriodEntry: PayPeriodEntry,
	statHolidays: StatHoliday[],
	exceptionTypes: ExceptionType[],
	isUserCDOEnrolled: boolean,
	cdoTemplateInfo: CDOTemplate,
	regularHoursDuration: number,
	modifiedDayDurationTemplate: ModifiedDurationDayTemplate
): TimeEntry[] => {
	let timeEntries: TimeEntry[] = [];

	let regularHoursEntries: TimeEntry = generateHoursEntryObject(exceptionTypes.find((exType) => exType.id === 1)!);
	let cdoHoursEntries: TimeEntry = generateHoursEntryObject(exceptionTypes.find((exType) => exType.id === 41)!);
	let cdoOnRollOverEntries: TimeEntry = generateHoursEntryObject(exceptionTypes.find((exType) => exType.id === 19)!);
	let statHoursEntries: TimeEntry = generateHoursEntryObject(exceptionTypes.find((exType) => exType.id === 28)!);
	let statOnCdoEntries: TimeEntry = generateHoursEntryObject(exceptionTypes.find((exType) => exType.id === 11)!);

	for (let i = 0; i < NUMBER_OF_TIME_ENTRIES_PER_TIMESHEET; i++) {
		let isStatHoliday = isStatHolidayDate(payPeriodEntry, i, statHolidays);
		let statHolidayName = isStatHoliday ? getStatHolidayName(payPeriodEntry, i, statHolidays) : '';

		if (dayMatchesPredictedWorkWeek(predictedWorkWeekPattern, payPeriodEntry, i)) {
			if (isUserCDOEnrolled) {
				if (isCDODate(cdoTemplateInfo, payPeriodEntry, i)) {
					if (isStatHoliday) {
						statOnCdoEntries.entries.push({ duration: regularHoursDuration, comment: statHolidayName, status: '' });
						cdoOnRollOverEntries.entries.push({ duration: 0, comment: '', status: '' });

						statHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
					} else {
						statOnCdoEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoOnRollOverEntries.entries.push({ duration: 0, comment: '', status: '' });

						statHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoHoursEntries.entries.push({ duration: regularHoursDuration, comment: '', status: '' });
					}

					regularHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
					cdoNeedsRollOver = isStatHoliday;
				} else {
					let timeEntry = isModifiedDurationDay(payPeriodEntry, i, modifiedDayDurationTemplate)
						? modifiedDayDurationTemplate.hours
						: regularHoursDuration;

					if (cdoNeedsRollOver && !isStatHoliday) {
						statOnCdoEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoOnRollOverEntries.entries.push({ duration: timeEntry, comment: '', status: '' });
						regularHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						statHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoNeedsRollOver = false;
					} else {
						regularHoursEntries.entries.push({ duration: isStatHoliday ? 0 : timeEntry, comment: '', status: '' });
						statHoursEntries.entries.push({ duration: isStatHoliday ? timeEntry : 0, comment: statHolidayName, status: '' });
						cdoHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
						statOnCdoEntries.entries.push({ duration: 0, comment: '', status: '' });
						cdoOnRollOverEntries.entries.push({ duration: 0, comment: '', status: '' });
					}
				}
			} else {
				regularHoursEntries.entries.push({ duration: isStatHoliday ? 0 : regularHoursDuration, comment: '', status: '' });
				statHoursEntries.entries.push({ duration: isStatHoliday ? regularHoursDuration : 0, comment: statHolidayName, status: '' });
				cdoHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
				statOnCdoEntries.entries.push({ duration: 0, comment: '', status: '' });
				cdoOnRollOverEntries.entries.push({ duration: 0, comment: '', status: '' });
			}
		} else {
			regularHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
			cdoHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
			statHoursEntries.entries.push({ duration: 0, comment: '', status: '' });
			statOnCdoEntries.entries.push({ duration: 0, comment: '', status: '' });
			cdoOnRollOverEntries.entries.push({ duration: 0, comment: '', status: '' });
		}
	}

	timeEntries.push(regularHoursEntries);
	timeEntries.push(statHoursEntries);

	if (isUserCDOEnrolled) {
		timeEntries.push(cdoHoursEntries);
		timeEntries.push(cdoOnRollOverEntries);
		timeEntries.push(statOnCdoEntries);
	}

	return timeEntries;
};

export const buildEntryTypeTime = (dayEntry: DailyTimeEntry): EntryTypeTime[] => {
	if (dayEntry) {
		const entryTypeTime: EntryTypeTime[] = [];

		dayEntry.entries.forEach((entry) =>
			entryTypeTime.push({
				entryType: getExceptionFromTypeId(entry.exceptionType.id),
				exceptionType: entry.exceptionType,
				entryTime: entry.duration,
				entryComment: entry.comment,
			})
		);

		return entryTypeTime;
	}

	return [];
};

export const textInputCleanup = (event: any, isPasteEvent: boolean, arr: any[], index: number, callbacks: Function[]) => {
	if (!isPasteEvent) {
		let text = String.fromCharCode(event.which);
		if ((event.target.value.includes('.') && text === '.') || !/^\d*\.?\d*$/.test(text)) {
			event.preventDefault();
		}
	} else {
		window.setTimeout(() => {
			let text = event.target.value;
			window.setTimeout(() => {
				if (!/^\d*\.?\d*$/.test(text)) {
					// let newString = event.target.value.replace(/^\d*\.?\d*$/g, '');
					let newString = event.target.value.replace(/[^\d.]/g, '');
					let splitStr = newString.split('.');

					if (splitStr.length === 1) {
						arr[index] = Number(splitStr[0]);
					} else {
						arr[index] = Number(`${splitStr[0]}.${splitStr.slice(1).join('')}`);
					}

					callbacks.forEach((callback) => callback());

					let inputs = event.target.closest('.entry-row').querySelectorAll('.entry-box-input');
					setTimeout(() => {
						let index = Array.prototype.indexOf.call(inputs, event.target) + 1;
						index = index === inputs.length ? 0 : index;
						inputs[index].focus();
					}, 0);
				}
			});
		});
	}
};

export const handleTextInputCleanup = (event: any, currentValue: number) => {
	var charCode = event.which ? event.which : event.keyCode;

	// Allowed charCodes for string text input for a number field
	let isDeleteKey = charCode === 46;
	let isBackspace = charCode === 8;
	let isFirstDecimal = (charCode === 190 || charCode === 110) && !currentValue.toString().includes('.');
	let isNumber = (charCode >= 48 && charCode <= 57) || (charCode >= 96 && charCode <= 105);
	let isArrowKeys = charCode >= 37 && charCode <= 40;
	let isHomeOrEndKeys = charCode === 35 || charCode === 36;
	let isAllowedSpecialKeys = charCode <= 31;

	if (
		(!isDeleteKey && !isBackspace && !isFirstDecimal && !isNumber && !isArrowKeys && !isHomeOrEndKeys && !isAllowedSpecialKeys) ||
		event.shiftKey
	) {
		event.preventDefault();
	}

	// if (charCode !== 46 && charCode !== 190 && charCode !== 110 && charCode > 31 && (charCode < 48 || (charCode > 57 && charCode < 96) || charCode > 105)) {
	//   event.preventDefault();
	// }
};

export const getExpectedDailyHours = (
	predictedWorkWeekPattern: WorkWeekPredictedPattern[],
	payPeriodEntry: PayPeriodEntry,
	isUserCDOEnrolled: boolean,
	cdoTemplateInfo: CDOTemplate,
	regularHoursDuration: number,
	modifiedDayDurationTemplate: ModifiedDurationDayTemplate,
	daysFromPayPeriodStart: number
): number => {
	if (dayMatchesPredictedWorkWeek(predictedWorkWeekPattern, payPeriodEntry, daysFromPayPeriodStart)) {
		if (isUserCDOEnrolled) {
			if (isCDODate(cdoTemplateInfo, payPeriodEntry, daysFromPayPeriodStart)) {
				return regularHoursDuration;
			} else {
				if (isModifiedDurationDay(payPeriodEntry, daysFromPayPeriodStart, modifiedDayDurationTemplate)) {
					return modifiedDayDurationTemplate.hours;
				} else {
					return regularHoursDuration;
				}
			}
		}

		return regularHoursDuration;
	}

	return 0;
};

// TODO write some tests for these PayPeriodEntry functions below

const _WeekOneIndex = [0, 1, 2, 3, 4, 5, 6];
const _WeekTwoIndex = [7, 8, 9, 10, 11, 12, 13];

export const updateTimeEntryInWeekOneOfPayPeriod = (
	payPeriod: PayPeriodEntry,
	action: any,
	timeEntryStatus: TimeEntryStatus,
	exceptionTypesMap: { [id: number]: ExceptionType }
) => {
	return _updateTimeEntryInWeekOfPayPeriodImplementation(payPeriod, action, _WeekOneIndex, timeEntryStatus, exceptionTypesMap);
};
export const updateTimeEntryInWeekTwoOfPayPeriod = (
	payPeriod: PayPeriodEntry,
	action: any,
	timeEntryStatus: TimeEntryStatus,
	exceptionTypesMap: { [id: number]: ExceptionType }
) => {
	return _updateTimeEntryInWeekOfPayPeriodImplementation(payPeriod, action, _WeekTwoIndex, timeEntryStatus, exceptionTypesMap);
};
const _updateTimeEntryInWeekOfPayPeriodImplementation = (
	payPeriod: PayPeriodEntry,
	action: any,
	indexes: number[],
	timeEntryStatus: TimeEntryStatus,
	exceptionTypesMap: { [id: number]: ExceptionType }
): PayPeriodEntry => {
	console.log(action);

	let oldEntryKeyMap = new Map<string, number>();
	let newEntryKeyMap = new Map<string, number>();

	const oldTimeEntries: { [id: string]: TimeEntry } = {};
	payPeriod.timeEntry.forEach((e: TimeEntry) => {
		const key = `${e.exceptionTypeId}_${e.projectId}`;

		if (oldEntryKeyMap.has(key)) oldEntryKeyMap.set(key, oldEntryKeyMap.get(key)! + 1);
		else oldEntryKeyMap.set(key, 1);

		const newKey = `${key}_${oldEntryKeyMap.get(key)}`;
		oldTimeEntries[newKey] = e;
	});
	const oldKeys = Object.keys(oldTimeEntries);

	const newTimeEntries: { [id: string]: TimeEntry } = {};
	action.timeEntries.forEach((e: TimeEntry) => {
		const key = `${e.exceptionTypeId}_${e.projectId}`;

		if (newEntryKeyMap.has(key)) newEntryKeyMap.set(key, newEntryKeyMap.get(key)! + 1);
		else newEntryKeyMap.set(key, 1);

		const newKey = `${key}_${newEntryKeyMap.get(key)}`;
		newTimeEntries[newKey] = e;
	});
	const newKeys = Object.keys(newTimeEntries);

	const timeEntries: TimeEntry[] = [];
	oldKeys.forEach((oldKey) => {
		if (newKeys.includes(oldKey)) {
			// Update existing TimeEntry - only update the index that belong to the current week
			const oldTimeEntry = oldTimeEntries[oldKey];
			const newTimeEntry = newTimeEntries[oldKey];
			const newEntries = oldTimeEntry.entries.map((entry, index) => {
				return {
					...entry,
					comment: indexes.includes(index) ? newTimeEntry.entries[index].comment : oldTimeEntry.entries[index].comment || '',
					status: indexes.includes(index) ? timeEntryStatus.toString() : oldTimeEntry.entries[index].status,
					duration: indexes.includes(index) ? newTimeEntry.entries[index].duration : oldTimeEntry.entries[index].duration,
				};
			});
			// console.log(oldTimeEntry);

			timeEntries.push({
				...oldTimeEntry,
				entries: newEntries,
			});
		} else {
			// Remove old TimeEntry
			// Do nothing
		}
	});

	newKeys.forEach((newKey) => {
		if (oldKeys.includes(newKey)) {
			// Entry already updated
			// Do nothing
		} else {
			// Add this new entry - use the full set of entries but only apply the status to the appropriate week
			const newTimeEntry = newTimeEntries[newKey];
			const newEntries = newTimeEntry.entries.map((entry, index) => {
				return {
					...entry,
					entryDate: getFutureDate(payPeriod.startDate, index),
					status: indexes.includes(index) ? timeEntryStatus.toString() : '',
				};
			});
			// console.log(newTimeEntry);

			timeEntries.push({
				...newTimeEntry,
				exceptionType: exceptionTypesMap[newTimeEntry.exceptionTypeId!],
				entries: newEntries,
			});
		}
	});

	return {
		...payPeriod,
		timeEntry: timeEntries,
	};
};

export const dateIsWithinPayPeriod = (payPeriod: PayPeriodEntry, date: Date) => {
	return dateIsWithinWeekOneOfPayPeriod(payPeriod, date) || dateIsWithinWeekTwoOfPayPeriod(payPeriod, date);
};
export const dateIsWithinWeekOneOfPayPeriod = (payPeriod: PayPeriodEntry, date: Date) => {
	const count = getDifferenceInDays(payPeriod.startDate, date);

	return count >= 0 && count <= 6;
};
export const dateIsWithinWeekTwoOfPayPeriod = (payPeriod: PayPeriodEntry, date: Date) => {
	const count = getDifferenceInDays(date, payPeriod.endDate);

	return count >= 0 && count <= 6;
};

export const timeEntryInWeekOneOfPayPeriod = (payPeriod: PayPeriodEntry): boolean => {
	return _timeEntryInWeekOfPayPeriodImplementation(payPeriod, _WeekOneIndex);
};
export const timeEntryInWeekTwoOfPayPeriod = (payPeriod: PayPeriodEntry): boolean => {
	return _timeEntryInWeekOfPayPeriodImplementation(payPeriod, _WeekTwoIndex);
};
const _timeEntryInWeekOfPayPeriodImplementation = (payPeriod: PayPeriodEntry, indexes: number[]): boolean => {
	if (payPeriod.timeEntry.length > 0) {
		return indexes.reduce((prev: boolean, atIndex: number) => {
			return prev || timeEntryAtIndexOfPayPeriod(payPeriod, atIndex);
		}, false);
	}

	return false;
};

export const timeEntryOnDateOfPayPeriod = (payPeriod: PayPeriodEntry, date: Date): boolean => {
	if (date < payPeriod.startDate || payPeriod.endDate < date) {
		return false;
	}

	const atIndex = getDifferenceInDays(payPeriod.startDate, date);
	return timeEntryAtIndexOfPayPeriod(payPeriod, atIndex);
};

export const timeEntryAtIndexOfPayPeriod = (payPeriod: PayPeriodEntry, index: number) => {
	return payPeriod.timeEntry.reduce((prev: boolean, current: TimeEntry) => {
		return prev || current.entries[index].duration > 0;
	}, false);
};

export const getDifferenceInDays = (smallerDate: string | Date, largerDate: string | Date) => {
	if (!smallerDate || !largerDate) {
		return 0;
	}

	const MS_PER_DAY = 1000 * 60 * 60 * 24;
	const date1 = new Date(getFutureDate(smallerDate, 0));
	const date2 = new Date(getFutureDate(largerDate, 0));

	const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
	const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());

	const difference = Math.floor((utc2 - utc1) / MS_PER_DAY);
	return difference;
};

export const convertSingleConstToDoubleDigitStr = (number: number) =>
	`${number.toLocaleString('en-US', {
		minimumIntegerDigits: 2,
		useGrouping: false,
	})}`;

export const ngbDateIsInPayPeriod = (targetDate: NgbDate, start: Date, end: Date) => {
	const ngbStart = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
	const ngbEnd = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
	if (targetDate.equals(ngbStart) || targetDate.equals(ngbEnd) || (targetDate.after(ngbStart) && targetDate.before(ngbEnd))) {
		return true;
	}
	return false;
};

export const getTimesheetPayPeriodInfo = (timesheet: Timesheet, payPeriods: PayPeriod[]) => {
	let payPeriod = payPeriods.find((pp) => pp.payPeriod === timesheet.payPeriod)!;
	const currentWeekYYYYMMDD = convertToYYYYMMDD(new Date(timesheet.startDate));
	const ppDateYYYYMMDD = convertToYYYYMMDD(new Date(payPeriod.startDate));

	if (currentWeekYYYYMMDD === ppDateYYYYMMDD) {
		return `Week 1 of Pay Period ${payPeriod.payPeriod.slice(4)}`;
	} else {
		return `Week 2 of Pay Period ${payPeriod.payPeriod.slice(4)}`;
	}
};

// Date -> yyyy-mm-dd
export const convertToStandardDateString = (date: Date): string => date.toISOString().split('T')[0];
// Date -> yyyymmdd
export const convertToYYYYMMDD = (date: Date): string => date.toISOString().replace(/-/g, '').slice(0, 8);

export const getFutureDate = (startingDateString: string | Date, numberOfDaysAhead: number) => {
	let startingUTCDate = new Date(startingDateString).toUTCString();
	let futureUTCDate = new Date(Date.parse(startingUTCDate) + numberOfDaysAhead * EPOCH_TIME_CONVERSION);
	let futureConvertedDate = new Date(convertToStandardDateString(futureUTCDate).replace(/-/g, '/'));
	return futureConvertedDate;
};

export const expandPayPeriodEntries = (payPeriodEntries: PayPeriodEntry[], exceptionTypes: ExceptionType[]): PayPeriodEntry[] => {
	const exceptionTypesMap: { [id: number]: ExceptionType } = Object.assign({}, ...exceptionTypes.map((e) => ({ [e.id]: e })));
	return payPeriodEntries.map((ppe) => {
		return {
			payPeriod: ppe.payPeriod,
			startDate: new Date(ppe.startDate),
			endDate: new Date(ppe.endDate),
			timeEntryCutOffDate: new Date(ppe.timeEntryCutOffDate),
			payDate: new Date(ppe.payDate),
			timeEntry: ppe.timeEntry.map((te) => expandTimeEntry(te, ppe.startDate, exceptionTypesMap)),
		};
	});
};

export const expandTimeEntry = (timeEntry: TimeEntry, startDate: string | Date, exceptionTypesMap: { [id: number]: ExceptionType }): TimeEntry => {
	const entries: { [id: string]: TimeEntryDetails } = {};
	timeEntry.entries.forEach((e) => {
		const date = new Date(e.entryDate || new Date(0));
		const key = convertToStandardDateString(date);
		entries[key] = {
			...e,
			entryDate: date,
		};
	});

	return {
		...timeEntry,
		exceptionType: exceptionTypesMap[timeEntry.exceptionTypeId!],
		entries: [...Array(14).keys()].map((i) => {
			const date = getFutureDate(startDate, i);
			const key = convertToStandardDateString(date);
			return entries[key] || { entryDate: date, duration: 0, status: '', comment: '' };
		}),
	};

	// return [...Array(14).keys()].map(i => {
	//     const date = getFutureDate(startDate, i);
	//     const key = convertToStandardDateString(date);
	//     return entries[key] || {entryDate: date, duration: 0, status: '', comment: ''};
	// });
};

export const buildDailyTimeEntryObject = (
	timeSheetEntry: TimeEntry,
	entryDetails: TimeEntryDetails,
	payPeriodEntry: PayPeriodEntry,
	payPeriodIndex: number,
	isPredictedData: boolean
) => {
	const entry: DailyTimeEntryDetails = {
		exceptionType: timeSheetEntry.exceptionType,
		projectId: timeSheetEntry.projectId,
		comment: entryDetails.comment || '',
		duration: entryDetails.duration,
		status: entryDetails.status,
		payPeriodEntry,
		payPeriodIndex,
		timeSheetEntry,
		isPredictedData,
	};

	return entry;
};

export const resetWorkWeekCalculationParameters = (
	numTimesWorkedPerDay: DaysWorkedCounter[],
	predictedWorkWeekPattern: WorkWeekPredictedPattern[]
) => {
	numTimesWorkedPerDay.forEach((entry) => (entry.numberTimesWorked = 0));
	predictedWorkWeekPattern.forEach((entry) => (entry.predictedWorkDay = false));
};

export const setWorkWeekPredictionPattern = (
	payPeriodEntries: PayPeriodEntry[],
	numTimesWorkedPerDay: DaysWorkedCounter[],
	predictedWorkWeekPattern: WorkWeekPredictedPattern[]
) => {
	const payPeriodData = payPeriodEntries.filter((payPeriod) => payPeriod.timeEntry.length > 0);

	// check for a length minimum of 5 since we dont store 0'd entries (weekends not stored)
	let shouldUseEntriesForPrediction =
		payPeriodData.length > 1 &&
		payPeriodData.some(
			(payPeriod) =>
				payPeriod.timeEntry.filter((entry) => getExceptionFromTypeId(entry.exceptionType.id) === TimesheetEntryType.REGULAR_DAY).length > 0 &&
				payPeriod.timeEntry
					.filter((entry) => getExceptionFromTypeId(entry.exceptionType.id) === TimesheetEntryType.REGULAR_DAY)[0]
					.entries.filter((entry) => entry.status === 'Approved' || entry.status === 'Sent to JDE').length >= 5 &&
				countNumDistinctDaysPopulated(
					payPeriod.timeEntry
						.filter((entry) => getExceptionFromTypeId(entry.exceptionType.id) === TimesheetEntryType.REGULAR_DAY)[0]
						.entries.map((entry) => entry.entryDate!)
				) >= 5
		);

	if (shouldUseEntriesForPrediction) {
		payPeriodData.forEach((payPeriodEntry) => {
			let timeEntries = payPeriodEntry.timeEntry;

			timeEntries
				.filter((timeEntry) => getExceptionFromTypeId(timeEntry.exceptionType.id) === TimesheetEntryType.REGULAR_DAY)
				.forEach((timeEntry) => {
					timeEntry.entries.forEach((entry) => {
						let entryDate = getFutureDate(entry.entryDate!, 0);
						let entryDay = entryDate.getDay();
						let isRegularWorkingDay = entry.duration > 0;

						if (isRegularWorkingDay) {
							numTimesWorkedPerDay[entryDay].numberTimesWorked++;
						}
					});
				});
		});
	}

	let results = numTimesWorkedPerDay.some((prediction) => prediction.numberTimesWorked > 0)
		? [...numTimesWorkedPerDay]
		: [
				{ day: 0, numberTimesWorked: 0 },
				{ day: 1, numberTimesWorked: 1 },
				{ day: 2, numberTimesWorked: 1 },
				{ day: 3, numberTimesWorked: 1 },
				{ day: 4, numberTimesWorked: 1 },
				{ day: 5, numberTimesWorked: 1 },
				{ day: 6, numberTimesWorked: 0 },
		  ];

	results.sort((a: DaysWorkedCounter, b: DaysWorkedCounter) => b.numberTimesWorked - a.numberTimesWorked);
	let slicedArr = results.slice(0, 5);

	slicedArr.forEach((dayEntry) => {
		predictedWorkWeekPattern.filter((predictionEntry) => predictionEntry.day === dayEntry.day)[0].predictedWorkDay = true;
	});
};

export const allUserTimesheetProjections = (
	userTimesheets: UserTimesheet[],
	payPeriods: PayPeriod[],
	stats: { [id: string]: StatHoliday },
	actingSchedule: ScheduledActingAssignment[]
): UserTimesheet[] => {
	let newUserTimesheets: UserTimesheet[] = [];
	userTimesheets.forEach((userTimesheet) => {
		let relativeActings = actingSchedule.filter((acting) => acting.forUserId === userTimesheet.userId);

		newUserTimesheets.push({
			...userTimesheet,
			timesheets: timesheetProjection(
				payPeriods,
				stats,
				{
					user: {
						firstName: '',
						lastName: '',
						upn: '',
						email: '',
						employeeNumber: '',
						active: false,
						isManager: false,
						isTimekeeper: false,
						isHR: false,
						id: userTimesheet.userId,
					},
					configuration: userTimesheet.configurations,
					manage: [],
					managedGoneFromDNV: [],
					delegated: [],
					delegatedFrom: [],
					delegatedTo: [],
					scheduledActings: [],
					allUsers: [],
					timekeeperDelegated: [],
					timekeeperManagedUsers: [],
				},
				userTimesheet.timesheets,
				relativeActings
			),
		});
	});

	return newUserTimesheets;
};

export const getBlankConfigurationProfile = (configurations: Configuration[]): Profile => {
	let userId = (configurations.length > 0 && configurations[0].userId) || -1;

	let tempProfile: Profile = {
		configuration: configurations,
		manage: [],
		managedGoneFromDNV: [],
		delegated: [],
		delegatedFrom: [],
		delegatedTo: [],
		scheduledActings: [],
		allUsers: [],
		timekeeperDelegated: [],
		timekeeperManagedUsers: [],
		user: getBlankUser(userId),
	};

	return tempProfile;
};

export const getBlankUser = (id: number = -1): User => {
	let tempUser: User = {
		id,
		firstName: '',
		lastName: '',
		upn: '',
		email: '',
		active: false,
		employeeNumber: '',
		isManager: false,
		isTimekeeper: false,
		isHR: false,
	};

	return tempUser;
};

export const timesheetProjection = (
	payPeriods: PayPeriod[],
	statHolidaysMap: { [id: string]: StatHoliday },
	profile: Profile,
	existingTimesheets: Timesheet[],
	scheduledActingAssignments: ScheduledActingAssignment[],
	isUserActive: boolean = true
): Timesheet[] => {
	let timesheets: Timesheet[] = [];

	payPeriods.forEach((pp, payPeriodIndex) => {
		const ppMetadata: TimesheetMetadata[] = [...Array(14).keys()].map((index) => {
			const ppDate: Date = addDays(pp.startDate as Date, index);
			const dateYYYYMMDD: string = convertToYYYYMMDD(ppDate);
			const statHoliday: StatHoliday | null | undefined = statHolidaysMap[dateYYYYMMDD];

			const configuration = configurationOn(profile.configuration, ppDate);
			const isUserAuxiliary = configuration.employmentStatus === EmploymentStatus.AUX;
			const cdoDayIndex = configuration.cdoDay - 1; // because cdoDay is in the [1, 14] range
			const isCDO = configuration.cdo && index === cdoDayIndex && !isUserAuxiliary;
			const modifiedDurationDayIndex = configuration.modifiedDurationDay - 1; // because modifiedDurationDay is in the [1, 14] range
			const isActing = scheduledActingAssignments.some(
				(acting) => getDateIsWithinBounds(acting.from, acting.to, ppDate) && acting.forUserId === profile.user.id
			);
			const isModifiedDurationDay = configuration.cdo && index === modifiedDurationDayIndex;
			const isWorkDay = isIndexWorkDay(index);

			let expectedDailyHours = 0;
			if (isWorkDay) {
				expectedDailyHours = isModifiedDurationDay ? configuration.durationOfModifiedDay : configuration.durationOfRegularDay;
			}

			return {
				isUserAuxiliary,
				isStat: !!statHoliday,
				isCDO, // Here, this CDO might be scheduled on a Stat
				expectedDailyHours,
				dateYYYYMMDD,
				statHoliday,
				actingMetadata: {
					isActing: isActing,
					actingIndex: scheduledActingAssignments.findIndex((acting) => getDateIsWithinBounds(acting.from, acting.to, ppDate)),
				},
			};
		});

		const ppEntries: { [id: string]: TimesheetEntry2 } = ppMetadata.reduce(
			(previousValue: { [id: string]: TimesheetEntry2 }, currentValue: TimesheetMetadata, currentIndex: number) => {
				if (currentValue.isStat) {
					if (!currentValue.isUserAuxiliary) {
						if (!previousValue.hasOwnProperty(TimesheetEntryType.STAT)) {
							previousValue[TimesheetEntryType.STAT] = newTimesheetEntry(TimesheetEntryType.STAT);
						}

						previousValue[TimesheetEntryType.STAT].durations[currentIndex] = currentValue.expectedDailyHours;
					}
				} else if (currentValue.isCDO) {
					if (!currentValue.isUserAuxiliary) {
						if (!previousValue.hasOwnProperty(TimesheetEntryType.CDO)) {
							previousValue[TimesheetEntryType.CDO] = newTimesheetEntry(TimesheetEntryType.CDO);
						}

						previousValue[TimesheetEntryType.CDO].durations[currentIndex] = currentValue.expectedDailyHours;
					}
				} else if (currentValue.actingMetadata.isActing) {
					if (!previousValue.hasOwnProperty(TimesheetEntryType.ACTING)) {
						previousValue[TimesheetEntryType.ACTING] = newTimesheetEntry(TimesheetEntryType.ACTING);
					}

					let actingRecord = scheduledActingAssignments[currentValue.actingMetadata.actingIndex];
					previousValue[TimesheetEntryType.ACTING].durations[currentIndex] = currentValue.expectedDailyHours;
					previousValue[TimesheetEntryType.ACTING].metadata.description = actingRecord.description;
					previousValue[TimesheetEntryType.ACTING].metadata.jobStep = actingRecord.jobStep;
					previousValue[TimesheetEntryType.ACTING].metadata.jobType = actingRecord.jobType;
					previousValue[TimesheetEntryType.ACTING].metadata.rate = actingRecord.rate;
				} else {
					if (!previousValue.hasOwnProperty(TimesheetEntryType.REGULAR_DAY)) {
						previousValue[TimesheetEntryType.REGULAR_DAY] = newTimesheetEntry(TimesheetEntryType.REGULAR_DAY);
					}

					if (!currentValue.isUserAuxiliary) {
						previousValue[TimesheetEntryType.REGULAR_DAY].durations[currentIndex] = currentValue.expectedDailyHours;
					}
				}

				return previousValue;
			},
			{}
		);

		// pp week 1
		const ppWeekOneStartDate = pp.startDate as Date;
		let weekOneTimesheet = {
			id: 0,
			userId: profile.user.id,
			startDate: ppWeekOneStartDate,
			endDate: addDays(ppWeekOneStartDate, 6),
			payPeriod: pp.payPeriod,
			payDate: new Date(pp.payDate),
			payPeriodStartDate: new Date(pp.startDate),
			payPeriodIndex: payPeriodIndex,
			status: TimesheetStatus.Predicted,
			metadata: ppMetadata.slice(0, 7),
			entries: Object.values(ppEntries).map((e) => {
				return Object.assign({}, e, { durations: e.durations.slice(0, 7) });
			}),
		};

		const ppWeekTwoStartDate = addDays(ppWeekOneStartDate, 7);
		let weekTwoTimesheet = {
			id: 0,
			userId: profile.user.id,
			startDate: ppWeekTwoStartDate,
			endDate: pp.endDate as Date,
			payPeriod: pp.payPeriod,
			payDate: new Date(pp.payDate),
			payPeriodStartDate: new Date(pp.startDate),
			payPeriodIndex: payPeriodIndex,
			status: TimesheetStatus.Predicted,
			metadata: ppMetadata.slice(7),
			entries: Object.values(ppEntries).map((e) => {
				return Object.assign({}, e, { durations: e.durations.slice(7) });
			}),
		};

		let timesheetsToAdd = [weekOneTimesheet, weekTwoTimesheet];
		timesheetsToAdd.forEach((t) => {
			// filter out time categories added but have no hours assigned to them
			let isUserAux = t.metadata.some((m) => m.isUserAuxiliary);
			if (!isUserAux) t.entries = t.entries.filter((e) => e.durations.some((d) => d > 0));
		});

		timesheets.push(...timesheetsToAdd);
	});

	let cdoMovedTimesheets = moveCdoScheduledOnStat(timesheets);
	let cdoMovedCorrectedTimesheets = moveExtendedDurationEntryOnCDO(cdoMovedTimesheets, profile);
	timesheets = cdoMovedCorrectedTimesheets;
	// timesheets = moveCdoScheduledOnStat(timesheets);

	if (existingTimesheets.length > 0) {
		existingTimesheets.forEach((existingTimesheet) => {
			let existingIndex = timesheets.findIndex(
				(timesheet) =>
					timesheet.payPeriod === existingTimesheet.payPeriod && timesheet.startDate.toString() === existingTimesheet.startDate.toString()
			);

			if (existingIndex !== -1) {
				let actingIndex = timesheets[existingIndex].entries.findIndex((ts) => ts.name === TimesheetEntryType.ACTING);
				let existingActingIndex = existingTimesheet.entries.findIndex((ts) => ts.name === TimesheetEntryType.ACTING);

				if (actingIndex !== -1 && existingActingIndex !== -1) {
					let existingCopy: Timesheet = {
						...existingTimesheet,
						entries: [...existingTimesheet.entries],
					};

					existingCopy.entries[existingActingIndex] = {
						...existingTimesheet.entries[existingActingIndex],
					};

					let predictedCopy: Timesheet = { ...timesheets[existingIndex] };

					let jobStep = predictedCopy.entries[actingIndex].metadata.jobStep;
					let jobType = predictedCopy.entries[actingIndex].metadata.jobType;
					let rate = predictedCopy.entries[actingIndex].metadata.rate;
					let description = predictedCopy.entries[actingIndex].metadata.description;

					if (!!existingCopy.entries[existingActingIndex].metadata.comments) {
						description += ` (${existingCopy.entries[existingActingIndex].metadata.comments})`;
					}

					let metadataCopy = {
						...existingCopy.entries[existingActingIndex].metadata,
						description,
						jobStep,
						jobType,
						rate,
					};

					existingCopy.entries[existingActingIndex].metadata = metadataCopy;

					timesheets[existingIndex] = {
						...existingCopy,
						payPeriodIndex: timesheets[existingIndex].payPeriodIndex,
					};
				} else {
					timesheets[existingIndex] = {
						...existingTimesheet,
						payPeriodIndex: timesheets[existingIndex].payPeriodIndex,
					};
				}
			} else {
				timesheets.push(existingTimesheet);
			}
		});
	}

	if (!isUserActive) {
		if (existingTimesheets.length === 0) return [];

		let sortedEmpTimesheets = [...existingTimesheets].sort((a: any, b: any) => a.startDate - b.startDate);

		let lastTimesheet = sortedEmpTimesheets[sortedEmpTimesheets.length - 1];
		let lastPayPeriodTimesheet = timesheets.filter((t) => t.payPeriod === lastTimesheet.payPeriod)[1];
		let indexLastNeededTimesheet = timesheets.findIndex((t) => t.startDate.getTime() === lastPayPeriodTimesheet.startDate.getTime());
		return timesheets.slice(0, indexLastNeededTimesheet + 1);
	}

	return timesheets;
};

export const generateWeekRangeString = (timesheet: Timesheet | null, includeYear: boolean = false): string => {
	if (!timesheet) {
		return '';
	}
	var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
	var startDate = new Date(timesheet.startDate);
	var endDate = new Date(timesheet.endDate);

	const weekStartMonth = monthNames[startDate.getMonth()];
	const weekEndMonth = monthNames[endDate.getMonth()];

	if (weekStartMonth !== weekEndMonth) {
		return includeYear
			? `${weekStartMonth} ${startDate.getDate()}, ${endDate.getFullYear()} - ${weekEndMonth} ${endDate.getDate()}, ${endDate.getFullYear()}`
			: `${weekStartMonth} ${startDate.getDate()} - ${weekEndMonth} ${endDate.getDate()}`;
	} else {
		return includeYear
			? `${weekStartMonth} ${startDate.getDate()}-${endDate.getDate()}, ${endDate.getFullYear()}`
			: `${weekStartMonth} ${startDate.getDate()} - ${endDate.getDate()}`;
	}
};

export const formatDurationForTimesheetDisplay = (number: number) => {
	let formattedNum = Intl.NumberFormat(navigator.language, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 2,
	}).format(number);

	return formattedNum;
};

function newTimesheetEntry(name: string): TimesheetEntry2 {
	return {
		name,
		metadata: {},
		durations: [...Array(14).keys()].map(() => 0),
	};
}
// initially just work on week days - can be enhanced later with recent booked schedule
export const isIndexWorkDay = (index: number) => {
	return ![0, 1, 7, 8].includes(index);
};

function moveCdoScheduledOnStat(timesheets: Timesheet[]): Timesheet[] {
	const reducerSeed: ReduceCdoScheduledOnStat = {
		timesheets: [],
		fromPreviousWeek: false,
	};

	let result: ReduceCdoScheduledOnStat = timesheets.reduce(
		(previousValue: ReduceCdoScheduledOnStat, currentValue: Timesheet, currentIndex: number, array: Timesheet[]): ReduceCdoScheduledOnStat => {
			let fromPreviousWeek = false;

			if (previousValue.fromPreviousWeek) {
				currentValue = insertCdoWithinTimesheet(currentValue);
			}

			const overlapAtIndex = currentValue.metadata.findIndex((m) => m.isStat && m.isCDO);
			if (overlapAtIndex >= 0) {
				const metadata = setCdoStatusAtIndex(currentValue.metadata, overlapAtIndex, false);
				currentValue = Object.assign({}, currentValue, { metadata });
				if (overlapAtIndex >= 6) {
					fromPreviousWeek = true;
				} else {
					currentValue = insertCdoWithinTimesheet(currentValue);
				}
			}

			return { timesheets: [...previousValue.timesheets, currentValue], fromPreviousWeek };
		},
		reducerSeed
	);

	return result.timesheets;
}

const moveExtendedDurationEntryOnCDO = (timesheets: Timesheet[], profile: Profile): Timesheet[] => {
	let correctedTimesheets: Timesheet[] = [];
	timesheets.forEach((timesheet) => {
		let configuration = configurationOn(profile.configuration, new Date(timesheet.startDate));
		if (!configuration.cdo) {
			correctedTimesheets.push(timesheet);
		} else {
			let cdoOnExtendedDurationHrs = timesheet.entries.some(
				(entry) =>
					entry.name === TimesheetEntryType.CDO && entry.durations.some((duration) => duration === configuration.durationOfModifiedDay)
			);

			if (!cdoOnExtendedDurationHrs) {
				correctedTimesheets.push(timesheet);
			} else {
				let regularEntryIndex = timesheet.entries.findIndex((e) => e.name === TimesheetEntryType.REGULAR_DAY);
				let actingEntryIndex = timesheet.entries.findIndex((e) => e.name === TimesheetEntryType.ACTING);

				let regularEntry: TimesheetEntry2 | null = null;
				let actingEntry: TimesheetEntry2 | null = null;

				if (regularEntryIndex !== -1) regularEntry = timesheet.entries[regularEntryIndex];
				if (actingEntryIndex !== -1) actingEntry = timesheet.entries[actingEntryIndex];

				let cdoEntryIndex = timesheet.entries.findIndex((e) => e.name === TimesheetEntryType.CDO);
				let cdoEntry = timesheet.entries[cdoEntryIndex];

				let extendedHrsIndex = cdoEntry.durations.findIndex((d) => d === configuration.durationOfModifiedDay);
				let entryDate = getFutureDate(timesheet.startDate, extendedHrsIndex);

				// TRUE - ExtendedHrs day is on a Friday / ExtendedHrs day is on Thursday with Friday STAT: Move CDO to the earliest non-stat day
				// FALSE - Otherwise: Move the extendedhrs day up one
				let shouldShiftCdoBackwards =
					entryDate.getDay() === DaysOfTheWeek.Friday ||
					(entryDate.getDay() === DaysOfTheWeek.Thursday &&
						timesheet.entries.some((e) => e.name === TimesheetEntryType.STAT && e.durations[DaysOfTheWeek.Friday] !== 0));

				if (shouldShiftCdoBackwards) {
					let isEntryActing = timesheet.metadata[extendedHrsIndex].actingMetadata.isActing;

					// check if the day in question is a regular day
					if (regularEntry !== null && !isEntryActing) {
						regularEntry.durations[extendedHrsIndex] = configuration.durationOfModifiedDay;
						timesheet.metadata[extendedHrsIndex].expectedDailyHours = configuration.durationOfModifiedDay;
					}

					// check if the day in question is for an acting role
					if (actingEntry !== null && isEntryActing) {
						actingEntry.durations[extendedHrsIndex] = configuration.durationOfModifiedDay;
						timesheet.metadata[extendedHrsIndex].expectedDailyHours = configuration.durationOfModifiedDay;
					}

					// reset the existing cdo entry to the default extended day config
					cdoEntry.durations[extendedHrsIndex] = 0;
					timesheet.metadata[extendedHrsIndex].isCDO = false;
					timesheet.metadata[extendedHrsIndex].expectedDailyHours = configuration.durationOfModifiedDay;

					// if the previous day is a stat, then set the shift index to be the day before the stat
					// let cdoShiftIndex = timesheet.metadata[extendedHrsIndex - 1].isStat ? extendedHrsIndex - 2 : extendedHrsIndex - 1;
					let cdoShiftIndex = extendedHrsIndex - 1;
					while (timesheet.metadata[cdoShiftIndex].isStat && cdoShiftIndex > 0) {
						cdoShiftIndex--;
					}

					// update the shifted cdo metadata and duration at the new index
					cdoEntry.durations[cdoShiftIndex] = configuration.durationOfRegularDay;
					timesheet.metadata[cdoShiftIndex].isCDO = true;
					timesheet.metadata[cdoShiftIndex].expectedDailyHours = configuration.durationOfRegularDay;
				} else {
					// update the cdo entry to be a regular day's hours and push the modified duration day to the next day
					cdoEntry.durations[extendedHrsIndex] = configuration.durationOfRegularDay;
					timesheet.metadata[extendedHrsIndex].expectedDailyHours = configuration.durationOfRegularDay;

					// if the following day is a stat, then find the closest day forwards that is not
					// let extendedShiftIndex = timesheet.metadata[extendedHrsIndex + 1].isStat ? extendedHrsIndex + 2 : extendedHrsIndex + 1;
					let extendedShiftIndex = extendedHrsIndex + 1;
					while (timesheet.metadata[extendedShiftIndex].isStat && extendedShiftIndex < DaysOfTheWeek.Friday) {
						extendedShiftIndex++;
					}

					let isEntryActing = timesheet.metadata[extendedShiftIndex].actingMetadata.isActing;

					// check if the day in question is a regular day
					if (regularEntry !== null && !isEntryActing) {
						regularEntry.durations[extendedShiftIndex] = configuration.durationOfModifiedDay;
						timesheet.metadata[extendedShiftIndex].expectedDailyHours = configuration.durationOfModifiedDay;
					}

					// check if the day in question is for an acting role
					if (actingEntry !== null && isEntryActing) {
						actingEntry.durations[extendedShiftIndex] = configuration.durationOfModifiedDay;
						timesheet.metadata[extendedShiftIndex].expectedDailyHours = configuration.durationOfModifiedDay;
					}
				}

				correctedTimesheets.push(timesheet);
			}
		}
	});

	return correctedTimesheets;
};

function insertCdoWithinTimesheet(currentValue: Timesheet): Timesheet {
	const insertAtIndex = currentValue.metadata.findIndex((m, i) => !m.isStat && !m.isCDO && isIndexWorkDay(i));
	const metadata = setCdoStatusAtIndex(currentValue.metadata, insertAtIndex, true);
	const duration = getDurationAtIndex(currentValue.entries, TimesheetEntryType.REGULAR_DAY, insertAtIndex);
	const entries_w_regular_zeroed_out: TimesheetEntry2[] = setDurationAtIndex(
		currentValue.entries,
		TimesheetEntryType.REGULAR_DAY,
		insertAtIndex,
		0
	);
	const entries: TimesheetEntry2[] = setDurationAtIndex(entries_w_regular_zeroed_out, TimesheetEntryType.CDO, insertAtIndex, duration);

	return Object.assign({}, currentValue, { metadata, entries });
}

function setCdoStatusAtIndex(metadata: TimesheetMetadata[], index: number, isCDO: boolean): TimesheetMetadata[] {
	return [...metadata.slice(0, index), Object.assign({}, metadata[index], { isCDO }), ...metadata.slice(index + 1)];
}
function getDurationAtIndex(entries: TimesheetEntry2[], name: string, durationAtIndex: number): number {
	let duration = 0;
	const entryAtIndex = entries.findIndex((e) => e.name === name);
	if (entryAtIndex >= 0) {
		duration = entries[entryAtIndex].durations[durationAtIndex];
	}
	return duration;
}
function setDurationAtIndex(entries: TimesheetEntry2[], name: string, durationAtIndex: number, duration: number): TimesheetEntry2[] {
	const entryAtIndex = entries.findIndex((e) => e.name === name);
	let entry: TimesheetEntry2;
	if (entryAtIndex >= 0) {
		entry = entries[entryAtIndex];
		let durations: number[] = [...entry.durations];
		durations[durationAtIndex] = duration;
		return [...entries.slice(0, entryAtIndex), Object.assign({}, entries[entryAtIndex], { durations }), ...entries.slice(entryAtIndex + 1)];
	} else {
		entry = newTimesheetEntry(name); // this returns a PP duration array of 14 days w 0 hours / day
		entry.durations = entry.durations.slice(0, 7); // take the first 7 days
		entry.durations[durationAtIndex] = duration;

		return [...entries, entry];
	}
}

export const getCurrentDayExpectedHours = (timesheet: Timesheet, configuration: Configuration, currentWeekDayIndex: number) => {
	let timesheetWeek = timesheetPayPeriodWeek(timesheet);
	let daysToAddToWeekDayIndex = timesheetWeek === '01' ? 0 : 7;
	let isModifiedDurationDay = configuration.cdo && currentWeekDayIndex + 1 + daysToAddToWeekDayIndex === configuration.modifiedDurationDay;
	let expectedDailyHours = isModifiedDurationDay ? configuration.durationOfModifiedDay : configuration.durationOfRegularDay;
	return expectedDailyHours;
};

// based on: https://stackoverflow.com/questions/563406/how-to-add-days-to-date
export const addDays = (date: Date, nbOfDays: number): Date => {
	let d = new Date(date.valueOf());
	d.setDate(d.getDate() + nbOfDays);
	return d;
};

export const getCurrentConfiguration = (configs: Configuration[]): Configuration => {
	const today = new Date();
	return configurationOn(configs, today);
};

export const getConfigurationOnDate = (configs: Configuration[], targetDate: Date): Configuration => configurationOn(configs, targetDate);

export const sortConfigurations = (configurations: Configuration[]): Configuration[] => {
	let sortedConfigs: Configuration[] = [...configurations].sort((a: Configuration, b: Configuration) => {
		let aStartDate = getBeginningOfDayDate(new Date(a.startingDate));
		let bStartDate = getBeginningOfDayDate(new Date(b.startingDate));

		if (aStartDate.getTime() > bStartDate.getTime()) return -1;
		if (aStartDate.getTime() < bStartDate.getTime()) return 1;

		let aCreatedDate = getBeginningOfDayDate(new Date(a.createdAt));
		let bCreatedDate = getBeginningOfDayDate(new Date(b.createdAt));

		if (aCreatedDate.getTime() > bCreatedDate.getTime()) return -1;
		if (aCreatedDate.getTime() < bCreatedDate.getTime()) return 1;
		return b.id - a.id;
	});

	return sortedConfigs;
};

export const configurationOn = (configurations: Configuration[], date: Date): Configuration => {
	// let configsOrdered = sortConfigurations(configurations).reverse();
	let configsOrdered = sortConfigurations(configurations);
	const end = new Date(9999, 11, 31);

	for (let index = 0; index < configsOrdered.length; index++) {
		const currentConfiguration = configsOrdered[index];
		const startDate = getBeginningOfDayDate(new Date(currentConfiguration.startingDate));
		let endDate = end;

		const nextIndex = index - 1;
		// if (nextIndex < configsOrdered.length) {
		if (nextIndex >= 0) {
			endDate = getBeginningOfDayDate(new Date(configsOrdered[nextIndex].startingDate));
		}

		if (date >= startDate && date < endDate) {
			return currentConfiguration;
		}
	}

	throw new Error('User configuration not found');
};

export const statTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration,
	siblingTsHasCDOEntry?: boolean
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));
	let scheduledCdoIsStat = false;

	if (configuration.cdo && isStatOnCDO(ts, configuration, dayOfWeekIndex)) {
		scheduledCdoIsStat = true;
		//   //Bank stat hour as CDO
		//   exceptionTypeId = this.exceptionTypes.find(e => e.code === 229)?.id ?? 0;
		//   prepTimeEntry.push({ duration: duration, comments: en.metadata.comments ?? '', exceptionTypeId: exceptionTypeId });
	}
	// if (configuration.employmentStatus === 'RFT') {
	//     //RFT stat
	//     exceptionTypeId = exceptionTypes.find(e => e.code === 530)?.id ?? 0;
	//     prepTimeEntry.push({ duration: duration, comments: `Stat Holiday`, exceptionTypeId: exceptionTypeId, entryDate: entryDate });
	// } else if (configuration.employmentStatus === 'TFT') {
	//     //TFT stat
	//     exceptionTypeId = exceptionTypes.find(e => e.code === 305)?.id ?? 0;
	//     prepTimeEntry.push({ duration: duration, comments: `Holiday Leave`, exceptionTypeId: exceptionTypeId, entryDate: entryDate });
	// }

	if (!scheduledCdoIsStat || siblingTsHasCDOEntry) {
		exceptionTypeId = exceptionTypes.find((e) => e.code === 530)?.id ?? 0;
		prepTimeEntry.push({
			duration: duration,
			comments: en.metadata.comments || `Stat Holiday`,
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
	}

	return prepTimeEntry;
};

export const isStatOnCDO = (ts: Timesheet, configuration: Configuration, dayOfStat: number) => {
	const ppWeek = timesheetPayPeriodWeek(ts);
	const isCDOStat =
		ppWeek === '01' ? (dayOfStat === configuration.cdoDay - 1 ? true : false) : dayOfStat === configuration.cdoDay / 2 - 1 ? true : false;
	const isCDOShifted = ts.entries.some(
		(entry) => entry.name === TimesheetEntryType.CDO && entry.durations.some((duration) => duration > 0) && entry.durations[dayOfStat] === 0
	);

	return isCDOStat && !isCDOShifted;
};

export const timesheetPayPeriodWeek = (ts: Timesheet): string => {
	const tsDate = new Date(ts.startDate);
	const ppDate = new Date(ts.payPeriodStartDate);
	// const ppDate = new Date(this.payPeriodEntries.find(ppe => ppe.payPeriod === ts.payPeriod)?.startDate ?? new Date());
	const tsDateYYYYMMDD = convertToYYYYMMDD(tsDate);
	const ppDateYYYYMMDD = convertToYYYYMMDD(ppDate);

	if (tsDateYYYYMMDD === ppDateYYYYMMDD) {
		return '01';
	} else {
		return '02';
	}
};

export const configurationCDOWeek = (configuration: Configuration): string => {
	let cdoWeek = '00';

	if (configuration.cdo) {
		if (configuration.cdoDay <= 7) {
			cdoWeek = '01';
		} else {
			cdoWeek = '02';
		}
	}
	return cdoWeek;
};

export const vacationTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	exceptionTypeId = exceptionTypes.find((e) => e.code === 300)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: en.metadata.comments || '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		approverId: ts.approvedBy,
	});

	return prepTimeEntry;
};

export const unionTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	if (en.metadata['options'].unionBusinessType === 'AtUnionExp') {
		exceptionTypeId = exceptionTypes.find((e) => e.code === 610)?.id ?? 0;
		prepTimeEntry.push({
			duration: duration,
			comments: en.metadata.comments || '',
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
	} else if (en.metadata['options'].unionBusinessType === 'AtDistrictExp') {
		exceptionTypeId = exceptionTypes.find((e) => e.code === 612)?.id ?? 0;
		prepTimeEntry.push({
			duration: duration,
			comments: en.metadata.comments || '',
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
	}

	return prepTimeEntry;
};

export const bankedTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration,
	prevYearOTHrsRemaining: number
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	switch (en.metadata['options'].bankedType) {
		case 'COT':
			let remainingDuration = duration;
			if (prevYearOTHrsRemaining > 0) {
				// calculate the correct amount of hours to extract and
				// subtract the duration used from the Previous OT Bank
				let durationToUse = remainingDuration > prevYearOTHrsRemaining ? prevYearOTHrsRemaining : remainingDuration;
				remainingDuration -= durationToUse;

				// add a previous OT overtime entry record
				exceptionTypeId = exceptionTypes.find((e) => e.code === 266)?.id ?? 0;
				prepTimeEntry.push({
					duration: durationToUse,
					comments: `POT Auto-Redemption${en.metadata.comments.length > 0 ? ` - (${en.metadata.comments})` : ''}`,
					exceptionTypeId: exceptionTypeId,
					entryDate: entryDate,
					isNew: ts.sendToJDEDateTime == null,
					timesheetId: ts.id,
					approverId: ts.approvedBy,
				});
			}

			if (remainingDuration > 0) {
				exceptionTypeId = exceptionTypes.find((e) => e.code === 263)?.id ?? 0;
				prepTimeEntry.push({
					duration: remainingDuration,
					comments: en.metadata.comments || '',
					exceptionTypeId: exceptionTypeId,
					entryDate: entryDate,
					isNew: ts.sendToJDEDateTime == null,
					timesheetId: ts.id,
					approverId: ts.approvedBy,
				});
			}

			break;
		case 'POT':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 266)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'CouncilOT':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 269)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'CDO':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 55)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'StatCDO':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 267)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
	}

	return prepTimeEntry;
};

// standby entries
export const standbyEntries = (ts: Timesheet, en: TimesheetEntry2, dayOfWeekIndex: number, exceptionTypes: ExceptionType[]) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	exceptionTypeId = exceptionTypes.find((e) => e.code === 750)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: en.metadata.comments || '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		approverId: ts.approvedBy,
	});

	return prepTimeEntry;
};

// partialDay for when duration < configuration.durationOfRegularDay
// [EmploymentStatus] based code RFT, TFT, AUX > 1, 2, 3
export const regularTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration,
	partialDay: boolean
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	let exceptionCode = 1; // default for RFT
	if (partialDay) {
		exceptionCode = 49;
	} else {
		switch (configuration.employmentStatus) {
			// EmploymentStatus.RPT ???
			case EmploymentStatus.RFT:
				exceptionCode = 1;
				break;
			case EmploymentStatus.TFT:
				exceptionCode = 2;
				break;
			case EmploymentStatus.AUX:
				exceptionCode = 3;
				break;
			default:
				exceptionCode = 1;
				break;
		}
	}
	exceptionTypeId = exceptionTypes.find((e) => e.code === exceptionCode)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		approverId: ts.approvedBy,
	});

	return prepTimeEntry;
};

export const sickTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	switch (en.metadata['options'].sickType) {
		case 'Family':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 402)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'Personal':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 400)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'UnpaidSick':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 405)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'WCBSick':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 450)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'BCBUnpaid':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 660)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
	}

	return prepTimeEntry;
};

export const otherTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	switch (en.metadata['options'].leaveType) {
		case 'Compassionate':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 605)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'JuryDuty':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 620)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'Maternity':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 650)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'Parental':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 652)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
		case 'Unpaid':
			exceptionTypeId = exceptionTypes.find((e) => e.code === 655)?.id ?? 0;
			prepTimeEntry.push({
				duration: duration,
				comments: en.metadata.comments || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
			break;
	}

	return prepTimeEntry;
};

export const actingTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	exceptionTypeId = exceptionTypes.find((e) => e.code === 11)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: en.metadata.comments || '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		rate: en.metadata.rate || '',
		jobStep: en.metadata.jobStep || '',
		jobType: en.metadata.jobType || '',
		approverId: ts.approvedBy,
	});
	return prepTimeEntry;
};

export const trainingTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	exceptionTypeId = exceptionTypes.find((e) => e.code === 600)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: en.metadata.comments || '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		approverId: ts.approvedBy,
	});
	return prepTimeEntry;
};

export const councilTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	exceptionTypeId = exceptionTypes.find((e) => e.code === 260)?.id ?? 0;
	prepTimeEntry.push({
		duration: duration,
		comments: en.metadata.comments || '',
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		isNew: ts.sendToJDEDateTime == null,
		timesheetId: ts.id,
		approverId: ts.approvedBy,
	});

	return prepTimeEntry;
};

export const otTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let comment = ``;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	if (en.metadata['options'].bankOrPayoutOT === 'BankIt') {
		exceptionTypeId = exceptionTypes.find((e) => e.code === 230)?.id ?? 0;
		comment = `Banked OT 1.5`;
	} else {
		exceptionTypeId = exceptionTypes.find((e) => e.code === 200)?.id ?? 0;
		comment = `Overtime 1.5`;
	}

	if (en.metadata['options'].prescheduledOT === 'PreSchdYes' && en.metadata['options'].adjacentOT === 'AdjOT') {
		if (duration < 2) {
			prepTimeEntry.push({
				duration: duration,
				comments: comment || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
		} else {
			const durationOT2 = duration - 2;
			prepTimeEntry.push({
				duration: 2,
				comments: comment || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});

			if (en.metadata['options'].bankOrPayoutOT === 'BankIt') {
				exceptionTypeId = exceptionTypes.find((e) => e.code === 239)?.id ?? 0;
				comment = `Banked OT 2.0`;
			} else {
				exceptionTypeId = exceptionTypes.find((e) => e.code === 209)?.id ?? 0;
				comment = `Overtime 2.0`;
			}

			if (durationOT2 > 0) {
				prepTimeEntry.push({
					duration: durationOT2,
					comments: comment || '',
					exceptionTypeId: exceptionTypeId,
					entryDate: entryDate,
					isNew: ts.sendToJDEDateTime == null,
					timesheetId: ts.id,
					approverId: ts.approvedBy,
				});
			}

			if (configuration.employmentStatus === 'RFT' || configuration.employmentStatus === 'TFT') {
				//.5 hour meal time for first 2 hour
				exceptionTypeId = exceptionTypes.find((e) => e.code === 215)?.id ?? 0;
				prepTimeEntry.push({
					duration: 0.5,
					comments: `Meal Break`,
					exceptionTypeId: exceptionTypeId,
					entryDate: entryDate,
					isNew: ts.sendToJDEDateTime == null,
					timesheetId: ts.id,
					approverId: ts.approvedBy,
				});

				//Add .5 meal time per 3.5 hours after the first 2 hours.
				const additionalMealTime = (duration - 2) / 3.5;
				for (var i = 1; i <= additionalMealTime; i++) {
					prepTimeEntry.push({
						duration: 0.5,
						comments: `Meal Break`,
						exceptionTypeId: exceptionTypeId,
						entryDate: entryDate,
						isNew: ts.sendToJDEDateTime == null,
						timesheetId: ts.id,
						approverId: ts.approvedBy,
					});
				}
			}
		}
	} else if (en.metadata['options'].prescheduledOT === 'PreSchdYes' && en.metadata['options'].adjacentOT === 'NonAdjOT') {
		if (en.metadata['options'].bankOrPayoutOT === 'BankIt') {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 239)?.id ?? 0;
			comment = `Banked OT 2.0`;
		} else {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 209)?.id ?? 0;
			comment = `Overtime 2.0`;
		}

		if (duration < 1.5) {
			prepTimeEntry.push({
				duration: 1.5,
				comments: comment || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
		} else {
			prepTimeEntry.push({
				duration: duration,
				comments: comment || '',
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});

			if (configuration.employmentStatus === 'RFT' || configuration.employmentStatus === 'TFT') {
				//Add .5 meal time per 3.5 hours after the first 2 hours.
				exceptionTypeId = exceptionTypes.find((e) => e.code === 215)?.id ?? 0;
				const additionalMealTime = duration / 3.5;
				for (var i = 1; i <= additionalMealTime; i++) {
					prepTimeEntry.push({
						duration: 0.5,
						comments: `Meal Break`,
						exceptionTypeId: exceptionTypeId,
						entryDate: entryDate,
						isNew: ts.sendToJDEDateTime == null,
						timesheetId: ts.id,
						approverId: ts.approvedBy,
					});
				}
			}
		}
	} else if (en.metadata['options'].prescheduledOT === 'PreSchdNo') {
		if (en.metadata['options'].bankOrPayoutOT === 'BankIt') {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 242)?.id ?? 0;
			comment = `Banked Callout 2.0`;
		} else {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 212)?.id ?? 0;
			comment = `Callout 2.0`;
		}

		prepTimeEntry.push({
			duration,
			comments: comment || '',
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
		if (duration >= 3 && (configuration.employmentStatus === 'RFT' || configuration.employmentStatus === 'TFT')) {
			//Add .5 meal time per 3.5 hours after the first 2 hours.
			exceptionTypeId = exceptionTypes.find((e) => e.code === 215)?.id ?? 0;
			const additionalMealTime = duration / 3.5;
			for (var i = 1; i <= additionalMealTime; i++) {
				prepTimeEntry.push({
					duration: 0.5,
					comments: `Meal Break`,
					exceptionTypeId: exceptionTypeId,
					entryDate: entryDate,
					isNew: ts.sendToJDEDateTime == null,
					timesheetId: ts.id,
					approverId: ts.approvedBy,
				});
			}
		}

		// if (duration < 3) {
		//     prepTimeEntry.push({ duration: 3, comments: comment || "", exceptionTypeId: exceptionTypeId, entryDate: entryDate, isNew: ts.sendToJDEDateTime == null, timesheetId: ts.id });

		// } else if (duration >= 3) {
		//         prepTimeEntry.push({ duration: duration + 1, comments: comment || "", exceptionTypeId: exceptionTypeId, entryDate: entryDate, isNew: ts.sendToJDEDateTime == null, timesheetId: ts.id });

		//     if ((configuration.employmentStatus === 'RFT' || configuration.employmentStatus === 'TFT')) {
		//         //Add .5 meal time per 3.5 hours after the first 2 hours.
		//         exceptionTypeId = exceptionTypes.find(e => e.code === 215)?.id ?? 0;
		//         const additionalMealTime = duration / 3.5;
		//         for (var i = 1; i <= additionalMealTime; i++) {
		//             prepTimeEntry.push({ duration: .5, comments: `Meal Break`, exceptionTypeId: exceptionTypeId, entryDate: entryDate, isNew: ts.sendToJDEDateTime == null, timesheetId: ts.id });
		//         }
		//     }
		// }
	}

	return prepTimeEntry;
};

export const bankCdoTimeEntries = (
	ts: Timesheet,
	day: TimesheetMetadata,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration,
	siblingTsHasCDOEntry?: boolean
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	// let expectedDailyHours = getCurrentDayExpectedHours(ts, configuration, dayOfWeekIndex);

	if (day.bankedCDO !== undefined && !siblingTsHasCDOEntry) {
		const cdoComments =
			day.bankedCDO?.options.bankType === 'Other'
				? day.bankedCDO?.options.bankType + ' ' + day.bankedCDO?.options.otherTypeText
				: day.bankedCDO?.options.bankType;

		if (day.isStat) {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 229)?.id ?? 0;
			prepTimeEntry.push({
				duration: configuration.durationOfRegularDay,
				// duration: expectedDailyHours,
				comments: cdoComments,
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
		} else if (!day.isStat) {
			exceptionTypeId = exceptionTypes.find((e) => e.code === 250)?.id ?? 0;
			prepTimeEntry.push({
				duration: configuration.durationOfRegularDay,
				// duration: expectedDailyHours,
				comments: cdoComments,
				exceptionTypeId: exceptionTypeId,
				entryDate: entryDate,
				isNew: ts.sendToJDEDateTime == null,
				timesheetId: ts.id,
				approverId: ts.approvedBy,
			});
		}
	}
	return prepTimeEntry;
};

// TODO We need to know here if there was CDO banked via code 250 or from 229 to choose from which bank to withdraw from.
export const withdrawCdoTimeEntries = (
	ts: Timesheet,
	en: TimesheetEntry2,
	dayOfWeekIndex: number,
	exceptionTypes: ExceptionType[],
	configuration: Configuration,
	usedBank: { [id: string]: number }
) => {
	let prepTimeEntry: TimeentryDB[] = [];
	let exceptionTypeId = 0;
	let duration = en.durations[dayOfWeekIndex];
	let entryDate = new Date(ts.startDate);
	entryDate = new Date(entryDate.setDate(entryDate.getDate() + dayOfWeekIndex));

	if (usedBank[BankType.StatutoryHolidays] >= duration - 0.00001) {
		// there is enough time in the Stat bank 229 -> 267
		exceptionTypeId = exceptionTypes.find((e) => e.code === 267)?.id ?? 0;
		prepTimeEntry.push({
			duration: duration,
			comments: `Stat CDO Bank`,
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
	} else {
		// else There is banked CDO time 250 -> 55
		exceptionTypeId = exceptionTypes.find((e) => e.code === 55)?.id ?? 0;
		prepTimeEntry.push({
			duration: duration,
			comments: `CDO Bank`,
			exceptionTypeId: exceptionTypeId,
			entryDate: entryDate,
			isNew: ts.sendToJDEDateTime == null,
			timesheetId: ts.id,
			approverId: ts.approvedBy,
		});
	}

	return prepTimeEntry;
};

export const correctionTimeEntries = (
	te: TimeentryDB[],
	ts: Timesheet,
	rbte: TimeentryDB[],
	ex: ExceptionType[],
	configuration: Configuration,
	startDate: Date,
	pp: PayPeriod[]
): TimeentryDB[] => {
	let correctedte: TimeentryDB[] = [];
	var employmentStatuses = ['', 'RFT', 'TFT', 'AUX']; //Order matters
	let correctionEntry: TimeentryDB;
	var otCodes = [200, 209, 212, 215, 230, 239, 242, 260];
	let otExceptionTypes = ex.filter((type) => otCodes.includes(type.code));
	var exceptionTypeId = employmentStatuses.indexOf(configuration.employmentStatus);

	// Look at a week worth of entries at a time.
	for (let i = 0; i < 7; i++) {
		let regularCorrection = 0;
		let currentDate = getFutureDate(new Date(startDate), i);
		const currentPP = getPayPeriodByDate(currentDate, pp);
		const modifiedDay = new Date(currentPP.startDate);
		modifiedDay.setDate(modifiedDay.getDate() + (configuration.modifiedDurationDay - 1));

		const teFiltered = te.filter((e) => e.entryDate.getTime() === currentDate.getTime());
		const rbteFiltered = rbte.filter((rbe) => rbe.entryDate.getTime() === currentDate.getTime());

		teFiltered.forEach((entry) => {
			var matchingRolledBackEntry = rbteFiltered.find((rbEntry) => entry.exceptionTypeId === rbEntry.exceptionTypeId);
			if (matchingRolledBackEntry !== undefined) {
				if (matchingRolledBackEntry.duration !== entry.duration || matchingRolledBackEntry.exceptionTypeId !== entry.exceptionTypeId) {
					//partial match
					if (matchingRolledBackEntry.duration !== entry.duration) {
						let duration = 0;
						duration = entry.duration - matchingRolledBackEntry.duration;
						correctionEntry = createTimeEntryObj(
							ts.id,
							entry.exceptionTypeId,
							entry.entryDate,
							entry.userId,
							duration,
							entry.projectId,
							entry.comments + '-CORRECTION',
							entry.workOrderNumber || '',
							false
						);
						if (entry.exceptionTypeId !== 42)
							// corrections for regular hour is added from RegularCorrection
							correctedte.push(correctionEntry);
					} else {
						if (entry.exceptionTypeId !== 42) {
							// corrections for regular hour is added from RegularCorrection
							correctedte.push(matchingRolledBackEntry);
						}
					}
				}
			} else if (matchingRolledBackEntry === undefined) {
				//new entries
				if (otExceptionTypes.findIndex((type) => type.id === entry.exceptionTypeId) > -1) {
					//New OT entry should always be added.
					correctedte.push(entry);
				} else {
					if (entry.exceptionTypeId !== 42) {
						correctedte.push(entry);
					}
				}
			}
		});

		rbteFiltered.forEach((rbEntry) => {
			var oldEntry = teFiltered.find((entry) => entry.exceptionTypeId === rbEntry.exceptionTypeId);

			//Found exceptions that got removed.
			if (oldEntry === undefined) {
				if (otExceptionTypes.findIndex((type) => type.id === rbEntry.exceptionTypeId) > -1) {
					//New OT entry should always be added.
					rbEntry.duration = rbEntry.duration * -1;
					rbEntry.comments += '-CORRECT';
					correctedte.push(rbEntry);
				} else {
					if (rbEntry.exceptionTypeId !== 42) {
						correctionEntry = createTimeEntryObj(
							ts.id,
							rbEntry.exceptionTypeId,
							rbEntry.entryDate,
							rbEntry.userId,
							rbEntry.duration * -1,
							rbEntry.projectId,
							rbEntry.comments + '-CORRECTION',
							rbEntry.workOrderNumber || '',
							false
						);
						correctedte.push(correctionEntry);
					}
				}
			}
		});

		//Look through current and previous timesheet to determine how to correct regular hours.
		var teExceptionTotal = teFiltered
			.filter(
				(e) =>
					!otExceptionTypes.find((type) => type.id === e.exceptionTypeId) &&
					e.exceptionTypeId !== 15 &&
					e.exceptionTypeId !== 42 &&
					e.exceptionTypeId !== 44
			)
			.reduce((sum, current) => sum + current.duration, 0);

		var rbExceptionTotal = rbteFiltered
			.filter(
				(e) =>
					!otExceptionTypes.find((type) => type.id === e.exceptionTypeId) &&
					e.exceptionTypeId !== 15 &&
					e.exceptionTypeId !== 42 &&
					e.exceptionTypeId !== 44
			)
			.reduce((sum, current) => sum + current.duration, 0);

		if (configuration.cdo && currentDate.getTime() === modifiedDay.getTime()) {
			regularCorrection = configuration.durationOfModifiedDay - rbExceptionTotal + teExceptionTotal - configuration.durationOfModifiedDay;
		} else {
			regularCorrection = configuration.durationOfRegularDay - rbExceptionTotal + teExceptionTotal - configuration.durationOfRegularDay;
		}

		if (regularCorrection !== 0) {
			correctionEntry = createTimeEntryObj(
				ts.id,
				exceptionTypeId,
				currentDate,
				rbte[0].userId,
				regularCorrection * -1,
				rbte[0].projectId,
				'-CORRECTION',
				'',
				false
			);
			correctedte.push(correctionEntry);
		}
	}

	return correctedte;
};

export const getPayPeriodByDate = (date: Date, pp: PayPeriod[]): PayPeriod => {
	const currentPP = pp.filter((pp) => {
		const ppStart = new Date(pp.startDate);
		const ppEnd = new Date(pp.endDate);

		if (ppStart.getTime() <= date.getTime() && date.getTime() <= ppEnd.getTime()) return pp;
		else return;
	});

	return currentPP[0];
};

const createTimeEntryObj = (
	timesheetId: number,
	exceptionTypeId: number,
	entryDate: Date,
	userId: number | undefined,
	duration: number,
	projectId: number | undefined,
	comments: string,
	workOrderNumber: string,
	isNew: boolean
): TimeentryDB => {
	return {
		timesheetId: timesheetId,
		exceptionTypeId: exceptionTypeId,
		entryDate: entryDate,
		userId: userId,
		duration: duration,
		projectId: projectId,
		comments: comments,
		workOrderNumber: workOrderNumber,
		isNew: isNew,
	};
};

export const addRegularHoursEntries = (
	ts: Timesheet,
	configurations: Configuration[],
	exceptionTypes: ExceptionType[],
	timeEntriesDBByPeriod: TimeentryDBByPeriod[]
) => {
	// const _options = Object.assign({ ignoreStatus: false, outputRegularEntries: false }, options);
	const configuration = configurationOn(configurations, ts.startDate);
	const prepTimeEntry: TimeentryDB[] = [];

	ts.entries.forEach((en) => {
		en.durations.forEach((duration, dayOfWeekIndex) => {
			if (duration !== 0) {
				let timeEntries: TimeentryDB[];
				switch (en.name) {
					case TimesheetEntryType.REGULAR_DAY:
						if (configuration.employmentStatus != EmploymentStatus.AUX) {
							timeEntries = regularTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, false);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});

							// ignore partial hour entries as those are covered under the regular partial day exception
							timeEntries = timeEntries.filter(
								(entry) =>
									(configuration.cdo &&
										(entry.duration === configuration.durationOfRegularDay ||
											entry.duration === configuration.durationOfModifiedDay)) ||
									(!configuration.cdo && entry.duration === configuration.durationOfRegularDay)
							);

							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
						}
						// else if (_options.outputRegularEntries || configuration.employmentStatus === EmploymentStatus.AUX) {
						//     timeEntries = regularTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, false);

						//     //TODO: use ID Timesheet userid
						//     timeEntries.map(entry => {
						//         entry.userId = configuration.userId;
						//         entry.comments = configuration.employmentStatus === EmploymentStatus.AUX ? 'Auxiliary' : '';
						//         return entry;
						//     })
						//     if (timeEntries.length > 0)
						//         timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }]
						//     prepTimeEntry.push(...timeEntries);
						// }
						break;
				}
			}
		});
	});

	return { prepTimeEntry: prepTimeEntry, timeEntriesByPeriod: timeEntriesDBByPeriod };
};

export const createTimeEntries = (
	ts: Timesheet,
	configurations: Configuration[],
	exceptionTypes: ExceptionType[],
	timeEntriesDBByPeriod: TimeentryDBByPeriod[],
	usedBank: { [id: string]: number },
	timeEntriesWithBatchNum: TimeentryDB[],
	options: {
		ignoreStatus?: boolean;
		outputRegularEntries?: boolean;
		rollingBackTimesheet?: Timesheet;
		siblingTimesheetEntry?: Timesheet;
		prevYearOTBank?: TimeBank;
	} = {}
) => {
	const _options = Object.assign({ ignoreStatus: false, outputRegularEntries: false }, options);
	const configuration = configurationOn(configurations, ts.startDate);
	const prepTimeEntry: TimeentryDB[] = [];

	let prevYearOTRemaining = options.prevYearOTBank?.allocated ? options.prevYearOTBank.allocated : 0;

	// const explicitRegularTimeEntries = false;
	// const exceptionTypeId = 0;

	var siblingTs = _options.siblingTimesheetEntry;
	var siblingTsHasCDOEntry =
		siblingTs !== null
			? siblingTs?.entries.some((entry) => entry.name === TimesheetEntryType.CDO && entry.durations.some((duration) => duration > 0))
			: false;

	if (_options.ignoreStatus || ts.status === 'Approved' || ts.status === 'JDE') {
		ts.entries.forEach((en) => {
			// let type = this.timeEntryTypes.find(t => t.entryText === en.name);
			let entryOptions = en.metadata['options'];
			let initialCdoEntryVisited = false;

			en.durations.forEach((duration, dayOfWeekIndex) => {
				let expectedDailyHours = getCurrentDayExpectedHours(ts, configuration, dayOfWeekIndex);

				if (duration !== 0) {
					let timeEntries: TimeentryDB[];

					switch (en.name) {
						case TimesheetEntryType.STAT:
							timeEntries = statTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, siblingTsHasCDOEntry);
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});

							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.STANDBY:
							timeEntries = standbyEntries(ts, en, dayOfWeekIndex, exceptionTypes);
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});

							if (timeEntries.length > 0) {
								timeEntriesDBByPeriod = [
									...timeEntriesDBByPeriod,
									{
										payPeriod: ts.payPeriod,
										timeEntries: timeEntries,
									},
								];
							}

							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.REGULAR_DAY:
							if (
								(duration < expectedDailyHours || entryOptions?.workOrderNumber?.length > 0) &&
								configuration.employmentStatus != EmploymentStatus.AUX
							) {
								timeEntries = regularTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, true);

								//TODO: use ID Timesheet userid
								timeEntries.map((entry) => {
									entry.userId = configuration.userId;
									entry.workOrderNumber = entryOptions?.workOrderNumber ? entryOptions.workOrderNumber : '';
									entry.comments = entryOptions?.workOrderDesc
										? entryOptions.workOrderDesc
										: en.metadata['comments']
										? en.metadata['comments']
										: '';
									return entry;
								});

								if (timeEntries.length > 0)
									timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
								prepTimeEntry.push(...timeEntries);
							} else if (_options.outputRegularEntries || configuration.employmentStatus === EmploymentStatus.AUX) {
								timeEntries = regularTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, false);

								//TODO: use ID Timesheet userid
								timeEntries.map((entry) => {
									let comments = configuration.employmentStatus === EmploymentStatus.AUX ? 'Auxiliary' : '';
									comments += comments.length > 0 ? ' - ' : '';
									comments += entryOptions?.workOrderDesc
										? entryOptions.workOrderDesc
										: en.metadata['comments']
										? en.metadata['comments']
										: '';

									entry.userId = configuration.userId;
									entry.workOrderNumber = entryOptions?.workOrderNumber ? entryOptions.workOrderNumber : '';
									entry.comments = comments;
									return entry;
								});
								if (timeEntries.length > 0)
									timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
								prepTimeEntry.push(...timeEntries);
							} else if (_options.rollingBackTimesheet && configuration.employmentStatus != EmploymentStatus.AUX) {
								let rbts = _options.rollingBackTimesheet;

								let hasRegEntry = rbts.entries.some((entry) => entry.name === TimesheetEntryType.REGULAR_DAY);
								let entryDurationDifferent =
									hasRegEntry &&
									rbts.entries.find((entry) => entry.name === TimesheetEntryType.REGULAR_DAY)?.durations[dayOfWeekIndex] !==
										duration;

								if ((hasRegEntry && entryDurationDifferent) || !hasRegEntry) {
									timeEntries = regularTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, true);

									//TODO: use ID Timesheet userid
									timeEntries.map((entry) => {
										entry.userId = configuration.userId;
										entry.workOrderNumber = entryOptions?.workOrderNumber ? entryOptions.workOrderNumber : '';
										entry.comments = entryOptions?.workOrderDesc
											? entryOptions.workOrderDesc
											: en.metadata['comments']
											? en.metadata['comments']
											: '';
										return entry;
									});

									if (timeEntries.length > 0)
										timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
									prepTimeEntry.push(...timeEntries);
								}
							}
							break;

						case TimesheetEntryType.CDO:
							// withdraw banked CDO when taking a CDO on a different day than the scheduled CDO day

							const metadata = ts.metadata[dayOfWeekIndex];
							const isScheduledCDO = metadata.isCDO && isStatOnCDO(ts, configuration, dayOfWeekIndex); // The timesheet meta CDO day is the same as the configuration scheduled cdo
							const isScheduledCDOWeek = timesheetPayPeriodWeek(ts) === configurationCDOWeek(configuration); // this timesheet is for the scheduled CDO week in the configuration
							const numberOfCdoDaysInTheWeek = en.durations.filter((d) => d > 0).length;

							// CDO on a different week than configured CDO -> always generate an entry
							// CDO during same week as configured CDO
							//  - and on the configured day -> no entry
							//  - but NOT on the configured day and there is only 1 CDO during the week -> no entry
							//  otherwise, generate an entry

							let generateEntry = true;
							if (isScheduledCDOWeek) {
								if (isScheduledCDO) {
									generateEntry = false;
									initialCdoEntryVisited = true;
								} else {
									if (numberOfCdoDaysInTheWeek == 1) generateEntry = false;
									if (numberOfCdoDaysInTheWeek > 1 && !initialCdoEntryVisited) generateEntry = false;
									initialCdoEntryVisited = true;
								}
							} else {
								// if the sibling timesheet has a bankedCDO entry, that means that the CDO was
								// shifted to the other week and no CDO entry is required so long as the scheduled week also
								// has no cdo entries
								var siblingCdoBanked = siblingTs?.metadata.some((meta) => meta.bankedCDO);
								var siblingTsCDOEntry = siblingTs?.entries.find(
									(e) => e.name === TimesheetEntryType.CDO && e.durations.some((d) => d > 0)
								);

								if (!siblingTsCDOEntry && siblingCdoBanked && !initialCdoEntryVisited) generateEntry = false;
								initialCdoEntryVisited = true;
							}

							if (generateEntry) {
								timeEntries = withdrawCdoTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, usedBank);

								//TODO: use ID Timesheet userid
								timeEntries.map((entry) => {
									entry.userId = configuration.userId;
									return entry;
								});
								if (timeEntries.length > 0)
									timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }]; //timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }]
								prepTimeEntry.push(...timeEntries);
							}
							break;

						case TimesheetEntryType.VACATION:
							timeEntries = vacationTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.OVERTIME:
							timeEntries = otTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								entry.workOrderNumber = entryOptions?.workOrderNumber ? entryOptions.workOrderNumber : '';
								entry.comments = entryOptions?.workOrderDesc ? entryOptions.workOrderDesc : entryOptions?.natureOfWorkOT;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.COUNCIL:
							timeEntries = councilTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.SICK:
							timeEntries = sickTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.ACTING:
							timeEntries = actingTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.BANKED:
							timeEntries = bankedTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration, prevYearOTRemaining);
							timeEntries.forEach((timeEntry) => {
								let prevYearOTExceptionType = exceptionTypes.find((e) => e.code === 266)?.id ?? 0;
								if (timeEntry.exceptionTypeId === prevYearOTExceptionType) {
									prevYearOTRemaining -= timeEntry.duration;
								}
							});

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.TRAINING:
							timeEntries = trainingTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.UNION:
							timeEntries = unionTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;

						case TimesheetEntryType.OTHER:
							timeEntries = otherTimeEntries(ts, en, dayOfWeekIndex, exceptionTypes, configuration);

							//TODO: use ID Timesheet userid
							timeEntries.map((entry) => {
								entry.userId = configuration.userId;
								return entry;
							});
							if (timeEntries.length > 0)
								timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
							prepTimeEntry.push(...timeEntries);
							break;
					}
				}
			});
		});

		// 'Bank' the scheduled CDO
		ts.metadata.forEach((day, index) => {
			let timeEntries = bankCdoTimeEntries(ts, day, index, exceptionTypes, configuration, siblingTsHasCDOEntry);
			timeEntries.map((entry) => {
				entry.userId = configuration.userId;
				return entry;
			});
			if (timeEntries.length > 0) timeEntriesDBByPeriod = [...timeEntriesDBByPeriod, { payPeriod: ts.payPeriod, timeEntries: timeEntries }];
			prepTimeEntry.push(...timeEntries);
		});
	}
	// this.timeEntryToClipboard(prepTimeEntry);

	// this.store.dispatch(SubmitTimeEntries({ timeEntries: prepTimeEntry}));

	// prepTimeEntry.map(en => formatDate(en.entryDate, 'yyyy-MM-dd', 'en-CA'))

	// populate batch numbers
	prepTimeEntry.forEach((entry) => {
		const batchNumber = timeEntriesWithBatchNum.find((entry) => entry.timesheetId === ts.id)?.batchNumber;
		if (batchNumber) entry.batchNumber = batchNumber;
	});

	return { prepTimeEntry: prepTimeEntry, timeEntriesByPeriod: timeEntriesDBByPeriod };
};

const updateUsed = (usedBank: { [id: string]: number }, bankType: BankType, timeEntry: TimeentryDB, multiplier: number) => {
	usedBank[bankType] += multiplier * timeEntry.duration;
};

export const compileUsedEntries = (bankRelatedTimeEntries: TimeentryDB[], bankTypes: TimeBankBankType[] | null = []): { [id: string]: number } => {
	const usedBank: { [id: string]: number } = {};
	if ((bankTypes as TimeBankBankType[]).length === 0) {
		return usedBank;
	}

	(bankTypes as TimeBankBankType[]).forEach((bt: TimeBankBankType) => {
		usedBank[bt.id] = 0;
	});

	bankRelatedTimeEntries.forEach((timeEntry) => {
		// Based on the [ExceptionType] table rows
		switch (timeEntry.exceptionTypeId) {
			case 1: // A Reg Pay - 1
			case 2: // A Temp F/T - 2
			case 3: // A Reg Other - 3
			case 4: // A Salary Act - 11
			// case 5: // CDO Leave - 55 --- Draw from bank
			case 6: // A Ot Pay 1.5 - 200
			case 7: // A Ot Pay 2.0 - 209
			case 8: // A Callout  2 - 212
			case 9: // A Meal Brk 2 - 215
			// case 10: // Bnk Stat 1.5 - 228 --- Add to bank
			// case 11: // Bnk Stat 1.0 - 229 --- Add to bank
			// case 12: // A Bk Ot 1.5 - 230 --- Add to bank
			// case 13: // A Bk Ot  2 - 239 --- Add to bank
			// case 14: // A Bk Co 2 - 242 --- Add to bank
			// case 15: // Banked CDO - 250 --- Add to bank
			// case 16: // D Council OT - 260 --- Add to bank
			// case 17: // A Cot Lv Pay - 263 --- Draw from bank
			// case 18: // A Pot Lv Pay - 266 --- Draw from bank
			// case 19: // A Stat Lv - 267 --- Draw from bank
			// case 20: // D Council Lv - 269 --- Draw from bank
			case 21: // A Vac Lv Pay - 300
			case 22: // A Hol Lv Pay - 305
			// case 23: // A Sck Lv - 400 --- Draw from bank
			// case 24: // D Family Lv - 402 --- Draw from bank
			case 25: // A Sck No Pay - 405
			case 26: // A Wcb Sck Lv - 450
			case 27: // DR ERB Lv Py - 515
			case 28: // A Stat Hol - 530
			case 29: // A Stat Wrkd - 531
			case 30: // D SDTB Lv - 535
			case 31: // A Train Lv - 600
			case 32: // A Compasn Lv - 605
			case 33: // A Gen Pd Lv - 607
			case 34: // A Union Lv - 610
			case 35: // A Jury Lv - 620
			case 36: // A Mat LvNP - 650
			case 37: // A Par LvNP - 652
			case 38: // A No Pay Lv - 655
			case 39: // A WCB No Pay - 660
			case 40: // A Shift Prem - 705
			case 41: // CDO Leave (Regular) - 9999 --- Scheduled CDO leave have no impact on bank
			case 42: // Regular Partial Day - 49
			case 43: // A UninLv Oth - 612
			case 44: // A Stndby Prem - 750
				break; // no bank impact

			// Add to bank

			// Bnk Stat 1.5 - 228
			case 10:
				updateUsed(usedBank, BankType.StatutoryHolidays, timeEntry, 1.5);
				break;
			// Bnk Stat 1.0 - 229
			case 11:
				updateUsed(usedBank, BankType.StatutoryHolidays, timeEntry, 1.0);
				break;
			// A Bk Ot 1.5 - 230
			case 12:
				updateUsed(usedBank, BankType.OvertimeCurrentYear, timeEntry, 1.5);
				break;
			// A Bk Ot  2 - 239
			case 13:
			// A Bk Co 2 - 242
			case 14:
				updateUsed(usedBank, BankType.OvertimeCurrentYear, timeEntry, 2.0);
				break;
			// Banked CDO - 250
			case 15:
				updateUsed(usedBank, BankType.CDO, timeEntry, 1.0);
				break;
			// D Council OT - 260
			case 16:
				updateUsed(usedBank, BankType.CouncilOT, timeEntry, 0.5);
				break;

			// Draw from bank

			// CDO Leave - 55
			case 5:
				updateUsed(usedBank, BankType.CDO, timeEntry, -1.0);
				break;
			// A Cot Lv Pay - 263
			case 17:
				updateUsed(usedBank, BankType.OvertimeCurrentYear, timeEntry, -1.0);
				break;
			// A Pot Lv Pay - 266
			case 18:
				updateUsed(usedBank, BankType.OvertimePreviousYear, timeEntry, -1.0);
				break;
			// A Stat Lv - 267
			case 19:
				updateUsed(usedBank, BankType.StatutoryHolidays, timeEntry, -1.0);
				break;
			// D Council Lv - 269
			case 20:
				updateUsed(usedBank, BankType.CouncilOT, timeEntry, -1.0);
				break;
			// A Sck Lv - 400
			case 23:
				updateUsed(usedBank, BankType.Sick, timeEntry, -1.0);
				break;
			// D Family Lv - 402
			case 24:
				updateUsed(usedBank, BankType.FamilyLeave, timeEntry, -1.0);
				break;

			default:
				console.error('Unexpected timeEntry ', timeEntry);
				break;
		}
	});

	return usedBank;
};
